/**
 * Live STAFF/ADMIN API (Cognito-authenticated). Relative `/api/requests...` and `/api/admin/...`.
 * Sends the Cognito ID token via Authorization (client.setAuthToken from the Amplify session).
 * Maps the deployed Go response shapes (handler/staff.go, handler/admin.go) → the view types the
 * console renders (api/types.ts). Reporter PII is decrypted server-side and only ever returned on
 * the single-request detail endpoint (NFR-9).
 */
import type { RequestStatus } from "@td/shared";
import { apiFetch, putToPresignedUrl, getAuthToken, ApiError } from "./client.js";
import type {
  AddSubscriberByStaffInput,
  AnalyticsView,
  AttachmentKind,
  AttachmentView,
  BGShareApprovalView,
  BGShareCandidateView,
  BGSharePhotoView,
  BulkItemResult,
  CategoryKind,
  CategoryView,
  CoachingState,
  CoachingStatePatch,
  ContractorDetailView,
  ContractorQueueRow,
  ContractorView,
  CouncilReportResult,
  CouncilReportScope,
  CreatePermitByStaffInput,
  CreatePermitByStaffResult,
  GroupView,
  InspectionTypeDef,
  NotificationTemplateView,
  NotificationTrigger,
  OcrPermitFieldsInput,
  OcrPermitFieldsResult,
  PermissionsRegistryModule,
  PutContractorInput,
  QueueRow,
  RenewContractorInput,
  ReporterContactView,
  SaveBGShareApprovalInput,
  SavedView,
  SavedViewFilters,
  StaffConcernsFilter,
  StaffConcernsView,
  StaffRequestDetail,
  StaffUserView,
  SubscriberView,
  SubscriptionStatus,
  Visibility,
} from "./types.js";
import type { SLAStage } from "../lib/sla.js";
import { DEFAULT_SLA_ACK_DAYS, DEFAULT_SLA_RESOLVE_DAYS } from "../lib/sla.js";

// ---- Wire shapes (Go JSON) -------------------------------------------------

interface WireRequest {
  /** Friendly category label (backend shared.ResolveCategoryLabel); may be absent on older responses. */
  categoryLabel?: string;
  id: string;
  trackingCode: string;
  category: string;
  description: string;
  status: RequestStatus;
  location: { address: string; lat?: number; lng?: number };
  assigneeId?: string | null;
  propertyId?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Reporter name/email, decrypted + projected by the console queue handler (staff.go). Absent on
   *  the detail endpoint + never in the export. */
  reporterName?: string;
  // SLA aging fields the queue endpoint embeds flat on each row (handler/staff.go ListQueue —
  // shared.SLAStatus embedded alongside shared.Request). Absent on the detail endpoint response.
  ageBusinessDays?: number;
  overdue?: boolean;
  slaStage?: SLAStage;
  targetDays?: number;
  /** Work-item due date (work-item-due-dates.md) — "YYYY-MM-DD", additive on every Kind; absent/""
   *  = no due date. Only ever staff-set/shown for a StaffOnly category. */
  dueDate?: string;
}

interface WireAudit {
  id: string;
  type: string;
  actorId?: string | null;
  /** "resident" for resident-authored events (e.g. resident_comment); empty/absent for staff/system
   *  events, which are attributed via actorId instead (shared/schemas.go AuditEvent.Source). */
  source?: string;
  visibility: Visibility;
  body: string;
  createdAt: string;
}

interface WireAttachment {
  id: string;
  s3Key: string;
  filename?: string;
  contentType?: string;
  kind: AttachmentKind;
  visibility: Visibility;
  uploaderId?: string | null;
  note?: string;
  trackingNumber?: string;
  createdAt: string;
  url?: string;
}

// ---- Queue (GET /api/requests) ---------------------------------------------

/**
 * `q` searches the FULL tenant result set server-side (description/address/tracking code/category
 * label — reporter PII is never a searchable field, shared.MatchesSearch) — not just the loaded
 * page. Omit for the unfiltered queue.
 */
export async function listQueueLive(q?: string): Promise<QueueRow[]> {
  const res = await apiFetch<{ requests: WireRequest[]; count: number }>("/requests", {
    auth: true,
    query: { q },
  });
  return res.requests.map((r) => ({
    id: r.id,
    trackingCode: r.trackingCode,
    category: r.category,
    categoryLabel: r.categoryLabel,
    description: r.description,
    status: r.status,
    address: r.location.address,
    // The queue list endpoint doesn't include children counts; the console handler decrypts + projects
    // the reporter NAME (not email/phone) so staff can see + search by reporter.
    attachmentCount: 0,
    reporterName: r.reporterName ?? null,
    assigneeId: r.assigneeId ?? null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    ageBusinessDays: r.ageBusinessDays ?? 0,
    overdue: r.overdue ?? false,
    slaStage: r.slaStage ?? "",
    targetDays: r.targetDays ?? 0,
    dueDate: r.dueDate || undefined,
  }));
}

// ---- Detail (GET /api/requests/{rid}) --------------------------------------

function mapAudit(e: WireAudit): StaffRequestDetail["events"][number] {
  const toStatus = e.type === "status_change" ? statusFromBody(e.body) : undefined;
  const isResident = e.source === "resident" || e.type === "resident_comment";
  return {
    id: e.id,
    type: (e.type as StaffRequestDetail["events"][number]["type"]) ?? "note",
    visibility: e.visibility,
    body: e.body,
    actorName: isResident
      ? "Resident"
      : (e.actorId ?? (e.type === "notification_sent" ? "System" : null)),
    toStatus,
    createdAt: e.createdAt,
  };
}

function mapAttachment(a: WireAttachment): AttachmentView {
  const isImage = (a.contentType ?? "").startsWith("image/") || a.kind.endsWith("photo");
  return {
    id: a.id,
    url: a.url ?? "",
    filename: a.filename || a.s3Key.split("/").pop() || "attachment",
    contentType: a.contentType ?? "",
    kind: a.kind,
    visibility: a.visibility,
    uploaderName: a.uploaderId ?? null,
    note: a.note,
    trackingNumber: a.trackingNumber,
    createdAt: a.createdAt,
    isImage,
  };
}

export async function getRequestLive(id: string): Promise<StaffRequestDetail | null> {
  let res: {
    request: WireRequest;
    // Top-level on the detail response (handler/staff.go GetRequest, shared.ResolveCategoryLabel) —
    // the friendly category label, e.g. "Junk / abandoned vehicle" for category id "junk-vehicle".
    categoryLabel?: string;
    reporterContact: ReporterContactView | null;
    events: WireAudit[];
    attachments: WireAttachment[];
  };
  try {
    res = await apiFetch(`/requests/${encodeURIComponent(id)}`, { auth: true });
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
  const r = res.request;
  return {
    id: r.id,
    trackingCode: r.trackingCode,
    category: r.category,
    categoryLabel: res.categoryLabel,
    description: r.description,
    status: r.status,
    location: r.location,
    reporterContact:
      res.reporterContact && !isEmptyContact(res.reporterContact) ? res.reporterContact : null,
    assigneeId: r.assigneeId ?? null,
    attachments: res.attachments.map(mapAttachment),
    events: res.events.map(mapAudit),
    // The detail endpoint returns propertyId; the prior-concerns list is a follow-up endpoint.
    property: null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    dueDate: r.dueDate || undefined,
  };
}

// ---- Staff-initiated intake (POST /api/requests) ---------------------------

/** Channel a staff-logged concern came in through (phone / walk-in / letter / email / other). */
export type IntakeChannel = "phone" | "walk_in" | "letter" | "email" | "other";

export interface CreateRequestInput {
  category: string;
  description: string;
  location: { address: string; lat?: number; lng?: number };
  attachments?: string[];
  reporterContact?: { name?: string; email?: string; phone?: string };
  intakeChannel: IntakeChannel;
  intakeNote?: string;
}

export interface CreateRequestResult {
  trackingCode: string;
  requestId: string;
  status: RequestStatus;
}

/**
 * Staff logs a concern taken by phone / walk-in / letter (POST /api/requests, Cognito-auth). The
 * body carries the reporter's contact (if given) + the intake channel/note for the audit trail.
 * Returns the tracking code the clerk reads back to the caller + the new request id for a deep link.
 */
export async function createRequestLive(input: CreateRequestInput): Promise<CreateRequestResult> {
  return apiFetch<CreateRequestResult>("/requests", {
    method: "POST",
    auth: true,
    body: input,
  });
}

// ---- Mutations -------------------------------------------------------------

export async function changeStatusLive(
  id: string,
  toStatus: RequestStatus,
  opts: { publicUpdate?: string; reason?: string },
): Promise<void> {
  await apiFetch(`/requests/${encodeURIComponent(id)}/status`, {
    method: "POST",
    auth: true,
    body: { status: toStatus, publicUpdate: opts.publicUpdate, reason: opts.reason },
  });
}

/**
 * `dueDate` (work-item-due-dates.md) is OPTIONAL and independent of assigneeId: omitting it
 * leaves any existing due date untouched (an assignee-only call never wipes it); "" clears it;
 * "YYYY-MM-DD" sets it. Same existing `/assign` endpoint + RBAC — no new route/permission.
 */
export async function assignRequestLive(
  id: string,
  assigneeId: string | null,
  dueDate?: string,
): Promise<void> {
  const body: { assigneeId: string | null; dueDate?: string } = { assigneeId };
  if (dueDate !== undefined) body.dueDate = dueDate;
  await apiFetch(`/requests/${encodeURIComponent(id)}/assign`, {
    method: "POST",
    auth: true,
    body,
  });
}

export async function addNoteLive(id: string, body: string, visibility: Visibility): Promise<void> {
  await apiFetch(`/requests/${encodeURIComponent(id)}/notes`, {
    method: "POST",
    auth: true,
    body: { body, visibility },
  });
}

/** Staff attach: presign (POST /api/uploads) → PUT to S3 → register (POST /api/uploads?mode=register). */
export async function addStaffAttachmentLive(
  requestId: string,
  file: File,
  kind: AttachmentKind,
  visibility: Visibility,
  onProgress?: (pct: number) => void,
): Promise<void> {
  const presign = await apiFetch<{ uploadUrl: string; s3Key: string }>("/uploads", {
    method: "POST",
    auth: true,
    body: { contentType: file.type, fileName: file.name, kind },
  });
  await putToPresignedUrl(presign.uploadUrl, file, file.type, onProgress);
  await apiFetch("/uploads", {
    method: "POST",
    auth: true,
    query: { mode: "register" },
    body: {
      requestId,
      s3Key: presign.s3Key,
      filename: file.name,
      contentType: file.type,
      kind,
      visibility,
    },
  });
}

// ---- Admin: categories / staff / settings ----------------------------------

interface WireCategory {
  id: string;
  label: string;
  order: number;
  active: boolean;
  slaAckDays?: number | null;
  slaResolveDays?: number | null;
  /** shared.Category.DefaultAssigneeID (services/api/internal/shared/schemas.go) — nil = no
   *  per-category routing. The admin write path validates this against ListStaff (active + same
   *  tenant) server-side; this client never needs to re-check it. */
  defaultAssigneeId?: string | null;
  /** shared.Category.Kind (ask-a-question-faq.md §2.1, stay-informed-channels.md §2.1) — "" (absent)
   *  reads as "concern". */
  kind?: string;
  /** shared.Category.DefaultOn (stay-informed-channels.md §2.1) — channel-only; absent/false for
   *  concern/question rows. */
  defaultOn?: boolean;
  /** shared.Category.StaffOnly (staff-only-concern-categories) — Kind=concern only; absent/false
   *  for every other row. Hidden from the public embed-config + rejected on public submit
   *  server-side; the admin/staff-create surface still sees + can select it. */
  staffOnly?: boolean;
  /** shared.Category.ShowOnMap (public-concerns-map.md / ADR-0023) — Kind=concern only; absent/false
   *  for every other row. Per-category opt-in to the public "Reported Concerns" map/feed. */
  showOnMap?: boolean;
  /** shared.Category.PermitAppFeeOverride — Kind=permit only. nil/omitted = inherit the tenant-wide
   *  default application fee; a number (including 0) overrides it for this permit type. */
  permitAppFeeOverride?: number | null;
  /** shared.Category.InspectionTypes (permit-inspections.md Phase 1) — Kind=permit only. The ordered
   *  expected-inspection checklist; nil/empty = no checklist (e.g. Water). */
  inspectionTypes?: InspectionTypeDef[];
}

function mapWireCategory(c: WireCategory): CategoryView {
  return {
    id: c.id,
    label: c.label,
    department: "",
    order: c.order,
    active: c.active,
    slaAckDays: c.slaAckDays ?? null,
    slaResolveDays: c.slaResolveDays ?? null,
    defaultAssigneeId: c.defaultAssigneeId ?? null,
    kind:
      c.kind === "question"
        ? "question"
        : c.kind === "channel"
          ? "channel"
          : c.kind === "kora"
            ? "kora"
            : c.kind === "case"
              ? "case"
              : c.kind === "permit"
                ? "permit"
                : "concern",
    defaultOn: c.defaultOn === true,
    staffOnly: c.staffOnly === true,
    showOnMap: c.showOnMap === true,
    permitAppFeeOverride: c.permitAppFeeOverride ?? null,
    inspectionTypes: c.inspectionTypes,
  };
}

/**
 * GET /admin/categories — ALL categories for the tenant, every Kind mixed (the server doesn't
 * filter; Category IS the topic taxonomy for concerns, questions, AND channels, §2.1). Pass `kind`
 * to filter client-side to just one taxonomy — e.g. CategoriesAdmin's concern/question/channel tabs,
 * or the staff question console's topic picker (listQuestionTopicsLive below).
 */
export async function listCategoriesLive(kind?: CategoryKind): Promise<CategoryView[]> {
  const res = await apiFetch<{ categories: WireCategory[] }>("/admin/categories", { auth: true });
  return res.categories
    .map(mapWireCategory)
    .filter((c) => !kind || c.kind === kind)
    .sort((a, b) => a.order - b.order);
}

/** Kind=question categories only — the live topic taxonomy for the staff question console /
 *  publish-to-FAQ composer (mirrors the public embed-config's questionTopics split, but staff-auth
 *  and unfiltered by active so an admin can still see a deactivated topic on an old question). */
export async function listQuestionTopicsLive(): Promise<CategoryView[]> {
  return listCategoriesLive("question");
}

/** Kind=kora categories only — the live record-type taxonomy for KORADetailPage / CategoriesAdmin's
 *  KORA tab (♻️ mirrors listQuestionTopicsLive; staff-auth, unfiltered by active). */
export async function listKORARecordTypesLive(): Promise<CategoryView[]> {
  return listCategoriesLive("kora");
}

/** Kind=case categories only — the live violation-type taxonomy for CaseOpenPage / CategoriesAdmin's
 *  "Violation types" tab (♻️ mirrors listKORARecordTypesLive; staff-auth, unfiltered by active). */
export async function listCaseViolationTypesLive(): Promise<CategoryView[]> {
  return listCategoriesLive("case");
}

/** The API persists one category per POST; save each changed row. */
export async function saveCategoriesLive(next: CategoryView[]): Promise<CategoryView[]> {
  for (const c of next) {
    await apiFetch("/admin/categories", {
      method: "POST",
      auth: true,
      body: {
        id: c.id,
        label: c.label,
        order: c.order,
        active: c.active,
        slaAckDays: c.slaAckDays ?? null,
        slaResolveDays: c.slaResolveDays ?? null,
        defaultAssigneeId: c.defaultAssigneeId ?? null,
        kind:
          c.kind === "question" ||
          c.kind === "channel" ||
          c.kind === "kora" ||
          c.kind === "case" ||
          c.kind === "permit"
            ? c.kind
            : undefined,
        defaultOn: c.kind === "channel" ? c.defaultOn === true : undefined,
        // Kind=concern only (or undefined, which reads as "concern" — see CategoryView.kind) — never
        // meaningful on question/channel/kora/case/permit rows.
        staffOnly: c.kind === "concern" || c.kind === undefined ? c.staffOnly === true : undefined,
        // Kind=concern only (public-concerns-map.md / ADR-0023) — never meaningful on any other row.
        showOnMap: c.kind === "concern" || c.kind === undefined ? c.showOnMap === true : undefined,
        // Kind=permit only — never meaningful on any other row. 0 must round-trip as 0, not be
        // treated as falsy/absent (mirrors slaAckDays/slaResolveDays's ?? null handling).
        permitAppFeeOverride: c.kind === "permit" ? (c.permitAppFeeOverride ?? null) : undefined,
        // Kind=permit only (permit-inspections.md Phase 1) — the expected-inspection checklist.
        // Send undefined (never an empty array) for every other kind so a concern/question/channel/
        // kora/case row can never pick up a stray inspectionTypes field.
        inspectionTypes: c.kind === "permit" ? (c.inspectionTypes ?? []) : undefined,
      },
    });
  }
  return listCategoriesLive();
}

export async function listStaffLive(): Promise<StaffUserView[]> {
  const res = await apiFetch<{
    staff: Array<{
      id: string;
      // Admin-only PII: GET /api/admin/staff omits email for non-admin STAFF callers.
      email?: string;
      name: string;
      role: "admin" | "staff";
      active: boolean;
      // Derived server-side from the Cognito UserStatus (handler/admin.go deriveStaffStatus) —
      // "invited" (provisioned, never signed in / legacy half-state row), "active" (CONFIRMED), or
      // "deactivated" (mirror inactive or Cognito account disabled). Falls back to the active flag
      // server-side if the ListUsers lookup fails, so this field is always present.
      status: "invited" | "active" | "deactivated";
      // RBAC (ADR-0016 §5): the server embeds shared.StaffUser (groupIds) + a derived
      // effectivePermissions on each admin entry. The Users & Permissions console reads groupIds to
      // render each user's role chips — omitting them here made every user show NO groups and made a
      // group assignment appear to revert on refresh (the write persisted; this read just dropped it).
      groupIds?: string[];
      effectivePermissions?: string[];
    }>;
  }>("/admin/staff", { auth: true });
  return res.staff.map((u) => ({
    id: u.id,
    name: u.name || null,
    email: u.email,
    role: u.role,
    status: u.status,
    // The DynamoDB staff mirror doesn't track MFA enrollment; Cognito is the source of truth.
    mfaEnrolled: u.role === "admin",
    groupIds: u.groupIds ?? [],
    effectivePermissions: u.effectivePermissions ?? [],
  }));
}

/**
 * TenantSettings as returned by GET /api/admin/settings (shared.TenantSettings). Branding
 * (townName/themeColor/logo/fonts), notification templates, and embed origins all live here; the
 * whole object is PUT back, so every write GET-merges to avoid clobbering fields it doesn't edit.
 */
interface WireSettings {
  tenantId?: string;
  townName?: string;
  themeColor?: string | null;
  /** Page background color pulled from the town's site (theming.md, resident branded palette). */
  backgroundColor?: string | null;
  logo?: string | null;
  /** `{display, body}` (either may be empty). A LEGACY plain string is also accepted from an older
   *  save and maps to BOTH pickers (BrandingAdmin `loaded.displayFont`/`bodyFont`, #172). */
  fonts?: { display?: string; body?: string } | string | null;
  townMailingAddress?: string | null;
  townPhone?: string | null;
  townEmail?: string | null;
  notificationTemplates?: Record<string, { subject: string; body: string }>;
  defaultAssignee?: string | null;
  allowedEmbedOrigins?: string[];
  /** Tenant BASE SLA targets (report-a-concern.md "SLA / aging indicators + reminders"); a missing
   *  field means "no override yet" — the API defaults to 2/10 (shared.SLATargets.Resolved). */
  sla?: { ackDays?: number; resolveDays?: number };
  /** Two town-configurable PUBLIC display toggles for the Council Agenda + Packet module
   *  (meetings-public-relaunch.md §3) — both default `true` (today's shipped behavior) when
   *  omitted/undefined, so an existing town's public page never changes on deploy. Enforced
   *  SERVER-SIDE in the public meeting detail projection (a `false` value OMITS the data, this
   *  admin toggle just reflects/edits the same tenant setting the server already reads). */
  publicShowAgendaItemDetails?: boolean;
  publicShowAttachments?: boolean;
  /** Per-town toggle for whether UNADOPTED ("draft") formal minutes show publicly at all, clearly
   *  labeled "pending Council approval" (recap/minutes independent-publish feature). Defaults `true`
   *  (today's shipped behavior — Minutes.Published alone gates visibility, no adoption dependency)
   *  when omitted/undefined. Enforced SERVER-SIDE (this admin toggle just reflects/edits the same
   *  tenant setting the server reads — shared.Minutes.MinutesPubliclyVisible). Does NOT affect the
   *  plain-language recap, which has no adoption gate. */
  publicShowDraftMinutes?: boolean;
  /** Permit → POA daily digest (permit-poa-digest.md) — off by default; a permit always reports on
   *  approval, this additionally sends a heads-up the moment one is first submitted. */
  permitDigestNotifyOnSubmit?: boolean;
  /** Any future fields survive the round-trip via the `...s` spread on save. */
  [key: string]: unknown;
}

/** The branding fields the admin edits — a subset of TenantSettings. */
export interface BrandingSettingsView {
  townName: string;
  themeColor: string | null;
  /** Page background color for the resident forms (theme/contrast.ts derives ink/surface from
   *  this client-side so the AA contrast guarantee holds no matter what the crawler pulls). */
  backgroundColor: string | null;
  logo: string | null;
  /** `{display, body}` — either may be empty string. A legacy plain string (pre-#172) is also
   *  accepted and applies to both pickers. */
  fonts: { display?: string; body?: string } | string | null;
  /** The {townMailingAddress} broadcast-footer slot (CAN-SPAM physical-address requirement). Set
   *  at runtime by the admin — never hardcoded per-town in this multi-tenant frontend. */
  townMailingAddress: string | null;
  /** The town's own public phone + email (city-hall contact) — surfaced on the printed agenda
   *  letterhead/footer. Admin-set, per-tenant; null when unset. */
  townPhone: string | null;
  townEmail: string | null;
  /** Base SLA targets, business days since submission (report-a-concern.md "SLA / aging
   *  indicators + reminders"). Defaults mirror the API: 2 (ack) / 10 (resolve). */
  slaAckDays: number;
  slaResolveDays: number;
  /** Council Agenda + Packet public-display toggles (meetings-public-relaunch.md §3) — resolved to
   *  their default-true value here so the admin UI never renders an ambiguous "unset" checkbox
   *  state; mirrors TenantSettings.ResolvedPublicShowAgendaItemDetails/ResolvedPublicShowAttachments. */
  publicShowAgendaItemDetails: boolean;
  publicShowAttachments: boolean;
  /** Resolved to its default-true value, mirroring publicShowAgendaItemDetails/publicShowAttachments
   *  above — see TenantSettings.ResolvedPublicShowDraftMinutes. */
  publicShowDraftMinutes: boolean;
  /** Permit → POA daily digest toggle (permit-poa-digest.md) — optional (defaults false when
   *  absent) so existing BrandingSettingsView literals elsewhere in the codebase don't need an
   *  unrelated update; getBrandingLive/saveBrandingLive always resolve it explicitly. */
  permitDigestNotifyOnSubmit?: boolean;
}

export async function getBrandingLive(): Promise<BrandingSettingsView> {
  const s = await apiFetch<WireSettings>("/admin/settings", { auth: true });
  return {
    townName: s.townName ?? "",
    themeColor: s.themeColor ?? null,
    backgroundColor: s.backgroundColor ?? null,
    logo: s.logo ?? null,
    fonts: s.fonts ?? null,
    townMailingAddress: s.townMailingAddress ?? null,
    townPhone: s.townPhone ?? null,
    townEmail: s.townEmail ?? null,
    slaAckDays: s.sla?.ackDays || DEFAULT_SLA_ACK_DAYS,
    slaResolveDays: s.sla?.resolveDays || DEFAULT_SLA_RESOLVE_DAYS,
    publicShowAgendaItemDetails: s.publicShowAgendaItemDetails !== false,
    publicShowAttachments: s.publicShowAttachments !== false,
    publicShowDraftMinutes: s.publicShowDraftMinutes !== false,
    permitDigestNotifyOnSubmit: s.permitDigestNotifyOnSubmit === true,
  };
}

/**
 * Save branding: GET the current settings, merge ONLY the branding fields, and PUT the whole object
 * back. This preserves notificationTemplates / allowedEmbedOrigins / defaultAssignee that this
 * surface never touches (the server replaces settings wholesale).
 */
export async function saveBrandingLive(
  branding: BrandingSettingsView,
): Promise<BrandingSettingsView> {
  const s = await apiFetch<WireSettings>("/admin/settings", { auth: true });
  const merged: WireSettings = {
    ...s,
    townName: branding.townName,
    themeColor: branding.themeColor,
    backgroundColor: branding.backgroundColor,
    logo: branding.logo,
    fonts: branding.fonts,
    townMailingAddress: branding.townMailingAddress,
    townPhone: branding.townPhone,
    townEmail: branding.townEmail,
    sla: { ackDays: branding.slaAckDays, resolveDays: branding.slaResolveDays },
    publicShowAgendaItemDetails: branding.publicShowAgendaItemDetails,
    publicShowAttachments: branding.publicShowAttachments,
    publicShowDraftMinutes: branding.publicShowDraftMinutes,
    permitDigestNotifyOnSubmit: branding.permitDigestNotifyOnSubmit,
  };
  await apiFetch("/admin/settings", { method: "PUT", auth: true, body: merged });
  return branding;
}

// ---- Branding logo (S3-hosted, ADR: logo-s3-hosting) -----------------------
//
// The logo used to ship INLINE as a data URL in TenantSettings.logo — a normal-resolution PNG
// pushed the settings PUT body past the 8 KB WAF SizeRestrictions_BODY limit, so logos never
// persisted. Fix: the logo bytes go straight to S3 (private bucket, served by CloudFront under
// /branding/*), and TenantSettings.logo stores only the short public PATH the settings PUT then
// carries (a few dozen bytes). A `data:` value is still accepted/rendered for tenants that saved a
// logo before this change (legacy inline).

export interface LogoUploadPresign {
  /** Presigned S3 PUT URL (~5 min expiry, size + content-type pinned server-side). */
  uploadUrl: string;
  /** S3 object key: branding/<tenantId>/logo-<hash>.<ext>. */
  key: string;
  /** Short path to store in TenantSettings.logo and to render via the public base URL. */
  publicPath: string;
}

/**
 * Ask the server to presign a logo upload (POST /api/admin/branding/logo-upload, adminAuth — the
 * tenantId comes from the staff auth context, never the body). `contentType` must be one of the
 * allowed image types; `extension` is a hint only (the server derives the authoritative one from
 * contentType) but keeping them in sync avoids a mismatched key.
 */
export async function presignLogoUploadLive(
  contentType: string,
  extension: string,
): Promise<LogoUploadPresign> {
  return apiFetch<LogoUploadPresign>("/admin/branding/logo-upload", {
    method: "POST",
    auth: true,
    body: { contentType, extension },
  });
}

/**
 * Full logo upload: presign → PUT the (already downscaled) blob straight to S3 → hand back the
 * short publicPath the caller sets as `form.logo` before Save. Save itself then persists only that
 * path through the existing settings PUT (saveBrandingLive) — no image bytes on that request.
 */
export async function uploadLogoLive(blob: Blob, extension: string): Promise<string> {
  const contentType = blob.type || "application/octet-stream";
  const presign = await presignLogoUploadLive(contentType, extension);
  await putToPresignedUrl(presign.uploadUrl, blob, contentType);
  return presign.publicPath;
}

// ---- Document store (S3-hosted, CloudFront /docs/*) -------------------------
//
// Mirrors the branding-logo upload flow above exactly (presign → PUT → short publicPath), but for
// staff-uploaded documents linked from FAQ answers (e.g. a PDF of a water-meter fee schedule): a
// NEW bucket/OAC/behavior (`docs`, `/docs/*`), a NEW admin-only endpoint
// (POST /api/admin/documents/upload), bigger size ceiling (~25 MB vs. logo's few MB), and a wider
// allowed content-type set (PDF + common image types) — see the document-store shared contract.

export interface DocumentUploadPresign {
  /** Presigned S3 PUT URL (short expiry, size + content-type pinned server-side). */
  uploadUrl: string;
  /** Short path to link to from a resident-facing FAQ answer, e.g. "/docs/<tenantId>/<name>". */
  publicPath: string;
}

/**
 * Ask the server to presign a document upload (POST /api/admin/documents/upload, adminAuth — the
 * tenantId comes from the staff auth context, never the body). `filename` is used server-side to
 * derive the sanitized filename segment of publicPath; `contentType` must be one of the allowed
 * document types (application/pdf, image/png, image/jpeg, image/webp, image/gif).
 */
export async function presignDocumentUploadLive(
  filename: string,
  contentType: string,
): Promise<DocumentUploadPresign> {
  return apiFetch<DocumentUploadPresign>("/admin/documents/upload", {
    method: "POST",
    auth: true,
    body: { filename, contentType },
  });
}

/**
 * Full document upload: presign → PUT the file straight to S3 → hand back the short publicPath the
 * caller inserts as a link href (RichTextEditor "Insert document"), OR — for a caller attaching the
 * uploaded object as a MeetingAttachment (s3Key) rather than linking to it — strip the leading "/" to
 * recover the raw S3 key. `onProgress` mirrors uploadRecordingLive's shape (percent 0-100, upload-only
 * — the presign call itself is comparatively instant and not separately reported).
 */
export async function uploadDocumentLive(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  const contentType = file.type || "application/octet-stream";
  const presign = await presignDocumentUploadLive(file.name, contentType);
  await putToPresignedUrl(presign.uploadUrl, file, contentType, onProgress);
  return presign.publicPath;
}

// ---- Staff accounts (GET/POST /api/admin/staff) ----------------------------

/** Wire shape the StaffUsers handler accepts/returns (shared.StaffUser mirror). `status` is the
 *  Cognito-derived field (handler/admin.go deriveStaffStatus) — present on GET responses; POST
 *  (invite/update/resend) echoes it back too, so mapStaff always has a real status to render.
 *  `role` includes "contributor" (agenda-packet.md §3.2 — the scoped Department Head / Contributor
 *  role; admin.go's IsValidStaffRole already accepts it) with a REQUIRED `department` on that role
 *  (the Contribution slot-matching key). */
interface WireStaffUser {
  id?: string;
  email: string;
  name: string;
  role: "admin" | "staff" | "contributor";
  active: boolean;
  status?: "invited" | "active" | "deactivated";
  department?: string;
  /** RBAC (ADR-0016 §5) — present on an admin caller's enriched GET /api/admin/staff row
   *  (handler/admin.go staffDirectoryAdminEntry embeds shared.StaffUser, whose GroupIDs JSON tag
   *  is "groupIds"); absent on the minimal non-admin projection. */
  groupIds?: string[];
  /** Same admin-only enrichment — the union of every group's Permissions (handler/admin.go
   *  effectivePermissionsList). */
  effectivePermissions?: string[];
}

function mapStaff(u: WireStaffUser & { id: string }): StaffUserView {
  return {
    id: u.id,
    name: u.name || null,
    email: u.email,
    role: u.role,
    // Prefer the live Cognito-derived status; fall back to the active flag only if the backend
    // response omits it (shouldn't happen post-#[this ticket], but keeps this defensive).
    status: u.status ?? (u.active ? "active" : "deactivated"),
    mfaEnrolled: u.role === "admin",
    department: u.department || undefined,
    groupIds: u.groupIds,
    effectivePermissions: u.effectivePermissions,
  };
}

/** Outcome the server reports for an invite (admin_staff_provision.go provisionOutcome) — lets the
 *  console pick "Invitation emailed" vs. "Already provisioned" toast copy without re-deriving it. */
export type InviteOutcome = "invited" | "already_provisioned";

export interface InviteStaffResult {
  staff: StaffUserView;
  outcome: InviteOutcome;
}

/** Invite/create a staff account (POST /api/admin/staff). Cognito is PROVISIONED first (AdminCreateUser
 *  + role-group membership, emailed invite) and is the source of truth; this writes/returns the
 *  DynamoDB mirror the console lists (ADR-0005, now automated on invite). Idempotent: re-inviting an
 *  email whose Cognito user already exists (or whose directory row exists with no Cognito user, the
 *  half-state) does not hard-fail — the server returns outcome "already_provisioned" instead.
 *  `department` is REQUIRED when `role === "contributor"` (admin.go accepts it on the StaffUser
 *  body) — the caller (StaffAdmin's Invite dialog) enforces this before calling. */
export async function inviteStaffLive(
  email: string,
  role: "admin" | "staff" | "contributor",
  name = "",
  department?: string,
): Promise<InviteStaffResult> {
  const res = await apiFetch<{
    staff: WireStaffUser & { id: string };
    outcome?: InviteOutcome;
  }>("/admin/staff", {
    method: "POST",
    auth: true,
    body: {
      email,
      name,
      role,
      active: true,
      department: role === "contributor" ? department : undefined,
    },
  });
  return { staff: mapStaff(res.staff), outcome: res.outcome ?? "invited" };
}

/** Outcome the server reports for a resend (admin.go resend branch): "resent" for a plain
 *  MessageAction=RESEND against an already-provisioned pending user, or "invited" when the row was
 *  a pre-#181 half-state row (a directory mirror row with no Cognito account yet) that Resend just
 *  provision-if-missing recovered — see admin_staff_provision.go resendCognitoStaffInvite. */
export type ResendOutcome = "resent" | "invited";

/** Resend the invite email for a still-pending (never-signed-in) staffer (POST /api/admin/staff with
 *  resend:true — reuses the existing route). PROVISION-IF-MISSING: if the Cognito user doesn't exist
 *  yet (a pre-#181 half-state directory row), the server falls back to a full provision instead of
 *  409ing — so this call always succeeds for any row the admin can see, and reports which happened. */
export async function resendStaffInviteLive(user: StaffUserView): Promise<ResendOutcome> {
  const res = await apiFetch<{ outcome?: ResendOutcome }>("/admin/staff", {
    method: "POST",
    auth: true,
    body: { id: user.id, email: user.email, resend: true },
  });
  return res.outcome ?? "resent";
}

/** Activate/deactivate a staff account by re-PUTting the mirror row with the new `active` flag. */
export async function setStaffActiveLive(
  user: StaffUserView,
  active: boolean,
): Promise<StaffUserView> {
  const res = await apiFetch<{ staff: WireStaffUser & { id: string } }>("/admin/staff", {
    method: "POST",
    auth: true,
    body: { id: user.id, email: user.email, name: user.name ?? "", role: user.role, active },
  });
  return mapStaff(res.staff);
}

/** Edit a staff account's name and/or role by re-PUTting the mirror row (server upserts by id). The
 *  active flag is preserved so an edit never silently reactivates/deactivates. `department` follows
 *  the effective role: required (and sent) when the effective role is "contributor", omitted
 *  otherwise so switching away from contributor clears it server-side. */
export async function updateStaffLive(
  user: StaffUserView,
  changes: { name?: string; role?: "admin" | "staff" | "contributor"; department?: string },
): Promise<StaffUserView> {
  const effectiveRole = changes.role ?? user.role;
  const res = await apiFetch<{ staff: WireStaffUser & { id: string } }>("/admin/staff", {
    method: "POST",
    auth: true,
    body: {
      id: user.id,
      email: user.email,
      name: (changes.name ?? user.name ?? "").trim(),
      role: effectiveRole,
      active: user.status !== "deactivated",
      department:
        effectiveRole === "contributor" ? (changes.department ?? user.department) : undefined,
    },
  });
  return mapStaff(res.staff);
}

/** Notification templates live on TenantSettings.notificationTemplates (GET/PUT /api/admin/settings). */

const TRIGGER_KEYS: Record<NotificationTrigger, string> = {
  confirmation: "confirmation",
  "status-update": "status-update",
  resolution: "resolution",
};

export async function listTemplatesLive(): Promise<NotificationTemplateView[]> {
  const s = await apiFetch<WireSettings>("/admin/settings", { auth: true });
  const t = s.notificationTemplates ?? {};
  return (Object.keys(TRIGGER_KEYS) as NotificationTrigger[]).map((trigger) => {
    const tpl = t[TRIGGER_KEYS[trigger]];
    return {
      trigger,
      emailSubject: tpl?.subject ?? "",
      emailBody: tpl?.body ?? "",
      smsBody: "",
      smsEnabled: false,
    };
  });
}

export async function saveTemplateLive(
  tpl: NotificationTemplateView,
): Promise<NotificationTemplateView> {
  // Merge into the existing settings and PUT the whole object (the API replaces settings wholesale).
  const s = await apiFetch<WireSettings>("/admin/settings", { auth: true });
  const templates = { ...(s.notificationTemplates ?? {}) };
  templates[TRIGGER_KEYS[tpl.trigger]] = { subject: tpl.emailSubject, body: tpl.emailBody };
  await apiFetch("/admin/settings", {
    method: "PUT",
    auth: true,
    body: { ...s, notificationTemplates: templates },
  });
  return tpl;
}

/** CSV export href (GET /api/admin/export.csv). The browser downloads with the auth header via fetch. */
export async function exportCsvLive(): Promise<Blob> {
  const res = await fetch("/api/admin/export.csv", {
    headers: authHeader(),
  });
  if (!res.ok) throw new Error(`Export failed (${res.status}).`);
  return res.blob();
}

// ---- Broadcast console (ADR-0012, notify/spec.md §4) ------------------------
//
// Endpoint list per the ticket: GET/POST /broadcasts, POST /broadcasts/{id}/send. Backend handlers
// are deployed and match shared.Broadcast (services/api/internal/shared/schemas.go) exactly.

import type {
  BroadcastDetail,
  BroadcastRow,
  CreateBroadcastInput,
  SendBroadcastResult,
} from "./types.js";

/**
 * Wire shape = shared.Broadcast (Go JSON tags) verbatim. `unsubscribedCount` is optional here
 * pending Inky's field landing on branch inky/notify-compliance-fixes (spec §4.1/§4.5) — the field
 * name is assumed to match the other *Count fields; flag to Inky if it ships under a different key.
 *
 * `channelEmail`/`channelWebsite`/`channelAlert`/`alertShowUntil`/`websitePostStatus` (turnkey-cms-ui.md
 * §5) are the PA announcements channel-flag fields — per the task brief, Inky lands the matching
 * `CreateBroadcastInput`/`Broadcast` fields on services/api + @td/shared IN PARALLEL. As of this
 * build that hasn't landed on this branch (no `channelEmail` et al. anywhere in services/api or
 * packages/shared here) — all five are OPTIONAL on the wire type and defaulted below
 * (`channelEmail` true / others false) so this compiles and degrades gracefully against the CURRENT
 * live API (which will simply ignore the extra JSON fields on write and omit them on read) until the
 * backend contract lands; verify field names match exactly once it does.
 */
interface WireBroadcast {
  id: string;
  tenantId: string;
  subject: string;
  body: string;
  audience: string;
  status: "draft" | "sending" | "sent";
  sentCount: number;
  bouncedCount: number;
  complainedCount: number;
  unsubscribedCount?: number;
  channelEmail?: boolean;
  channelWebsite?: boolean;
  channelAlert?: boolean;
  alertShowUntil?: string | null;
  websitePostStatus?: "posted" | "failed" | "removed-early";
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  sentAt?: string | null;
}

function mapBroadcastRow(b: WireBroadcast): BroadcastRow {
  return {
    id: b.id,
    subject: b.subject,
    status: b.status,
    audience: b.audience,
    sentCount: b.sentCount,
    bouncedCount: b.bouncedCount,
    complainedCount: b.complainedCount,
    unsubscribedCount: b.unsubscribedCount,
    channelEmail: b.channelEmail ?? true,
    channelWebsite: b.channelWebsite ?? false,
    channelAlert: b.channelAlert ?? false,
    alertShowUntil: b.alertShowUntil ?? null,
    websitePostStatus: b.websitePostStatus,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
    sentAt: b.sentAt ?? null,
  };
}

function mapBroadcastDetail(b: WireBroadcast): BroadcastDetail {
  return { ...mapBroadcastRow(b), body: b.body, createdBy: b.createdBy };
}

/** GET /broadcasts (Cognito-auth) — list drafts + sent (spec §4.1). */
export async function listBroadcastsLive(): Promise<BroadcastRow[]> {
  const res = await apiFetch<{ broadcasts: WireBroadcast[] }>("/broadcasts", { auth: true });
  return res.broadcasts.map(mapBroadcastRow);
}

/** GET /broadcasts/{id} (Cognito-auth) — full detail incl. body (compose/preview/sent-detail). */
export async function getBroadcastLive(id: string): Promise<BroadcastDetail | null> {
  try {
    const res = await apiFetch<{ broadcast: WireBroadcast }>(
      `/broadcasts/${encodeURIComponent(id)}`,
      { auth: true },
    );
    return mapBroadcastDetail(res.broadcast);
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
}

/** POST /broadcasts (create or save-draft — same endpoint; an id in the body updates in place).
 *  `audience` ("all_confirmed_subscribers" | "channel:<id>", stay-informed-channels.md §2.3/§3.3) is
 *  optional — omitted means "all", unchanged v1 default; the server re-validates it against the
 *  tenant's active channels and silently falls back to "all" rather than erroring (broadcasts.go
 *  resolveChannelAudience), so a stale/deleted-channel selection can never break a draft save. */
export async function saveBroadcastDraftLive(
  input: CreateBroadcastInput,
  id?: string,
): Promise<BroadcastDetail> {
  const res = await apiFetch<{ broadcast: WireBroadcast }>("/broadcasts", {
    method: "POST",
    auth: true,
    body: {
      id,
      subject: input.subject,
      body: input.body,
      audience: input.audience,
      channelEmail: input.channelEmail,
      channelWebsite: input.channelWebsite,
      channelAlert: input.channelAlert,
      alertShowUntil: input.alertShowUntil,
    },
  });
  return mapBroadcastDetail(res.broadcast);
}

/** POST /broadcasts/{id}/send (Cognito-auth) — enqueues the send (spec §4.4); the dialog waits for
 *  THIS call to resolve before showing "Sending started" (not optimistic). */
export async function sendBroadcastLive(id: string): Promise<SendBroadcastResult> {
  return apiFetch<SendBroadcastResult>(`/broadcasts/${encodeURIComponent(id)}/send`, {
    method: "POST",
    auth: true,
  });
}

/** POST /broadcasts/{id}/test (spec §4.3 "Send yourself a test") — sends the rendered email to the
 *  signed-in staffer's own address. */
export async function sendBroadcastTestLive(id: string): Promise<void> {
  await apiFetch(`/broadcasts/${encodeURIComponent(id)}/test`, { method: "POST", auth: true });
}

// ---- Saved views (GET/POST /api/saved-views, DELETE /api/saved-views/{id}) -
//
// PRIVATE per staff user: the server scopes every read/write to the caller's own Cognito sub
// (never a body-supplied owner), so one staffer never sees or deletes another's saved views.

interface WireSavedView {
  id: string;
  name: string;
  filters: SavedViewFilters;
  createdAt: string;
}

function mapSavedView(v: WireSavedView): SavedView {
  return { id: v.id, name: v.name, filters: v.filters ?? {}, createdAt: v.createdAt };
}

export async function listSavedViewsLive(): Promise<SavedView[]> {
  const res = await apiFetch<{ savedViews: WireSavedView[]; count: number }>("/saved-views", {
    auth: true,
  });
  return res.savedViews.map(mapSavedView);
}

export async function saveSavedViewLive(
  name: string,
  filters: SavedViewFilters,
): Promise<SavedView> {
  const res = await apiFetch<WireSavedView>("/saved-views", {
    method: "POST",
    auth: true,
    body: { name, filters },
  });
  return mapSavedView(res);
}

export async function deleteSavedViewLive(id: string): Promise<void> {
  await apiFetch(`/saved-views/${encodeURIComponent(id)}`, { method: "DELETE", auth: true });
}

// ---- Bulk queue actions (POST /api/requests/bulk/assign, /api/requests/bulk/status) ---------
//
// Tenant + acting user are always resolved server-side from the JWT. Failures are per-id — a
// batch never aborts partway; the caller sees exactly which ids succeeded.

export async function bulkAssignLive(
  requestIds: string[],
  assigneeId: string | null,
): Promise<BulkItemResult[]> {
  const res = await apiFetch<{ results: BulkItemResult[] }>("/requests/bulk/assign", {
    method: "POST",
    auth: true,
    body: { requestIds, assigneeId },
  });
  return res.results;
}

export async function bulkChangeStatusLive(
  requestIds: string[],
  status: RequestStatus,
  publicUpdate?: string,
): Promise<BulkItemResult[]> {
  const res = await apiFetch<{ results: BulkItemResult[] }>("/requests/bulk/status", {
    method: "POST",
    auth: true,
    body: { requestIds, status, publicUpdate },
  });
  return res.results;
}

// ---- helpers ---------------------------------------------------------------

function isEmptyContact(c: ReporterContactView): boolean {
  return !c.name && !c.email && !c.phone;
}

function authHeader(): Record<string, string> {
  const t = getAuthToken();
  return t ? { authorization: t } : {};
}

/** Status is not returned on audit events directly; infer for the timeline pill from the label. */
function statusFromBody(body: string): RequestStatus | undefined {
  const map: Record<string, RequestStatus> = {
    New: "New",
    Acknowledged: "Acknowledged",
    "In Progress": "InProgress",
    Resolved: "Resolved",
    Closed: "Closed",
    Duplicate: "Duplicate",
    "Invalid / Rejected": "Invalid",
  };
  for (const [label, status] of Object.entries(map)) {
    if (body.includes(label)) return status;
  }
  return undefined;
}

// ---- Analytics (GET /api/analytics, Cognito-auth, tenant-scoped from the JWT) ---------------
//
// AGGREGATE-ONLY: the server never returns reporter PII, contact info, or individual complaint
// descriptions on this endpoint — every field is a count, average, or labeled bucket. The tenant
// is resolved from the Cognito claims server-side; `start`/`end` are the only client inputs.

export async function getAnalyticsLive(start?: string, end?: string): Promise<AnalyticsView> {
  return apiFetch<AnalyticsView>("/analytics", {
    auth: true,
    query: { start, end },
  });
}

// ---- Branding crawler (POST /api/admin/branding/crawl) ---------------------

/**
 * The branding-suggestion payload from POST /api/admin/branding/crawl (handler/branding.go →
 * crawl.Result). Every field is best-effort: present when detected, null when not. These are
 * SUGGESTIONS ONLY — the crawler never writes TenantSettings; the admin reviews + applies each in
 * the Branding form, which still runs the contrast guard + font allow-list before Save.
 */
export interface BrandingCrawlResult {
  themeColor: string | null;
  /** Detected page background color (theming.md resident branded palette), or null. */
  backgroundColor: string | null;
  fonts: string | null;
  /** Detected logo as a data URL (already size-capped server-side), or null. */
  logo: string | null;
  logoSourceUrl: string | null;
  sourceUrl: string;
  notes: string[];
}

/**
 * Ask the server to fetch the town's existing public site and suggest branding. The fetch is
 * SSRF-hardened server-side. Error codes surface as ApiError.code: `blocked_url` (SSRF guard),
 * `fetch_failed` (unreachable / parse), `url_required` / `invalid_body` (bad input) — the UI maps
 * these to plain, actionable messages.
 */
export async function crawlBrandingLive(url: string): Promise<BrandingCrawlResult> {
  const res = await apiFetch<Partial<BrandingCrawlResult>>("/admin/branding/crawl", {
    method: "POST",
    auth: true,
    body: { url },
  });
  return {
    themeColor: res.themeColor ?? null,
    backgroundColor: res.backgroundColor ?? null,
    fonts: res.fonts ?? null,
    logo: res.logo ?? null,
    logoSourceUrl: res.logoSourceUrl ?? null,
    sourceUrl: res.sourceUrl ?? url,
    notes: res.notes ?? [],
  };
}

// ---- Ask a Question + FAQ: staff console + admin FAQ (ask-a-question-faq.md) -----------------
//
// Mirrors the concern queue/detail live-mapping conventions above: WireX interfaces for the Go
// JSON shape, mapped to the view types the console renders. Same Cognito auth posture (staffAuth
// for the question console, adminAuth for the FAQ publish/edit surface — handler/question.go,
// handler/admin_faq.go).

import type {
  FAQAnalytics,
  FAQView,
  PublishFAQInput,
  QuestionQueueRow,
  QuestionStatus,
  StaffQuestionDetail,
  SuggestFAQDraft,
  UpdateFAQInput,
} from "./types.js";

interface WireQuestion {
  id: string;
  trackingCode: string;
  category: string; // Category reused as the topic id (§2.1)
  topicLabel?: string;
  description: string; // the raw ask text (Description field reused, §5.1)
  status: QuestionStatus;
  answer?: string;
  answeredAt?: string;
  faqId?: string;
  assigneeId?: string | null;
  reporterContact?: ReporterContactView | null;
  reporterName?: string;
  createdAt: string;
  updatedAt: string;
}

function mapQuestionQueueRow(r: WireQuestion): QuestionQueueRow {
  return {
    id: r.id,
    trackingCode: r.trackingCode,
    topic: r.category,
    topicLabel: r.topicLabel,
    question: r.description,
    status: r.status,
    // Console handler decrypts + projects the asker NAME (or email); mocks may carry a full contact.
    reporterName: r.reporterName ?? r.reporterContact?.name ?? null,
    assigneeId: r.assigneeId ?? null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

/** GET /api/admin/questions — the question console queue (♻️ list-queue shape, Kind=question). */
export async function listQuestionQueueLive(q?: string): Promise<QuestionQueueRow[]> {
  const res = await apiFetch<{ questions: WireQuestion[]; count: number }>("/admin/questions", {
    auth: true,
    query: { q },
  });
  return res.questions.map(mapQuestionQueueRow);
}

interface WireQuestionEvent {
  id: string;
  type: string;
  actorId?: string | null;
  source?: string;
  visibility: Visibility;
  body: string;
  createdAt: string;
}

/**
 * GET /api/admin/questions/{id} — no dedicated single-question detail route is in the Appendix
 * table; the question console fetches the queue and finds the row it needs, then hydrates the
 * detail from the same `/requests/{id}` staff detail endpoint the concern console uses (a Question
 * IS a Request — same storage, same read path). Maps the Request-shaped response into the
 * question-shaped view the console renders.
 */
export async function getQuestionLive(id: string): Promise<StaffQuestionDetail | null> {
  // GET /requests/{id} (handler.GetRequest) returns the Request fields NESTED under `request`, with
  // categoryLabel / reporterContact / events at the TOP level (same shape getRequestLive reads). A
  // question IS a Request, so we hydrate from the same endpoint — but must read request fields off
  // `res.request`, not the root, or status/createdAt come back undefined (Invalid Date + no answer
  // form) while the top-level reporterContact still resolves.
  let res: {
    request: WireQuestion;
    categoryLabel?: string;
    reporterContact: ReporterContactView | null;
    events?: WireQuestionEvent[];
  };
  try {
    res = await apiFetch(`/requests/${encodeURIComponent(id)}`, { auth: true });
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
  const r = res.request;
  return {
    id: r.id,
    trackingCode: r.trackingCode,
    topic: r.category,
    topicLabel: res.categoryLabel ?? r.topicLabel,
    question: r.description,
    status: r.status,
    answer: r.answer ?? "",
    answeredAt: r.answeredAt ?? null,
    faqId: r.faqId ?? null,
    reporterContact: res.reporterContact ?? null,
    assigneeId: r.assigneeId ?? null,
    events: (res.events ?? []).map((e) => ({
      id: e.id,
      type: (e.type as StaffQuestionDetail["events"][number]["type"]) ?? "note",
      visibility: e.visibility,
      body: e.body,
      actorName: e.source === "resident" ? "Resident" : (e.actorId ?? null),
      createdAt: e.createdAt,
    })),
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

/** POST /api/admin/questions/{id}/answer — write the public answer, New -> Answered, notify. */
export async function answerQuestionLive(id: string, answer: string): Promise<void> {
  await apiFetch(`/admin/questions/${encodeURIComponent(id)}/answer`, {
    method: "POST",
    auth: true,
    body: { answer },
  });
}

/** POST /api/admin/questions/{id}/close — Answered -> Closed (not FAQ-worthy). */
export async function closeQuestionLive(id: string): Promise<void> {
  await apiFetch(`/admin/questions/${encodeURIComponent(id)}/close`, {
    method: "POST",
    auth: true,
  });
}

interface WireFAQAdmin {
  id: string;
  topicId: string;
  topicLabel?: string;
  question: string;
  answerHtml: string;
  published: boolean;
  featured: boolean;
  order: number;
  viewCount: number;
  helpfulYes: number;
  helpfulNo: number;
  sourceQuestionIds?: string[];
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

function mapAdminFAQ(f: WireFAQAdmin): FAQView {
  return { ...f };
}

/** GET /api/admin/faq — list ALL, incl. unpublished (FAQ admin table). */
export async function listAdminFAQLive(): Promise<FAQView[]> {
  const res = await apiFetch<{ faqs: WireFAQAdmin[] }>("/admin/faq", { auth: true });
  return res.faqs.map(mapAdminFAQ);
}

/** POST /api/admin/faq — publish (+ duplicate→merge). Server-sanitizes answerHtml (NFR-3). */
export async function publishFAQLive(
  input: PublishFAQInput,
): Promise<{ faq: FAQView; mergedQuestionIds: string[] }> {
  const res = await apiFetch<{ faq: WireFAQAdmin; mergedQuestionIds: string[] }>("/admin/faq", {
    method: "POST",
    auth: true,
    body: input,
  });
  return { faq: mapAdminFAQ(res.faq), mergedQuestionIds: res.mergedQuestionIds };
}

/** PUT /api/admin/faq/{id} — edit/unpublish/reorder/feature. */
export async function updateFAQLive(id: string, input: UpdateFAQInput): Promise<FAQView> {
  const res = await apiFetch<{ faq: WireFAQAdmin }>(`/admin/faq/${encodeURIComponent(id)}`, {
    method: "PUT",
    auth: true,
    body: input,
  });
  return mapAdminFAQ(res.faq);
}

/** POST /api/admin/concerns/suggest-faq — cross-module "Turn into a FAQ" draft prefill. */
export async function suggestFAQFromConcernLive(requestId: string): Promise<SuggestFAQDraft> {
  return apiFetch<SuggestFAQDraft>("/admin/concerns/suggest-faq", {
    method: "POST",
    auth: true,
    body: { requestId },
  });
}

/** FAQ analytics block — rides GET /api/analytics's "faq" field (§5.5), aggregate-only. */
export async function getFAQAnalyticsLive(): Promise<FAQAnalytics | undefined> {
  const res = await apiFetch<{ faq?: FAQAnalytics }>("/analytics", { auth: true });
  return res.faq;
}

// ---- Open Records (KORA) requests: staff console (records-request.md) ------------------------
//
// Mirrors the question console's live-mapping conventions above: WireX interfaces for the Go JSON
// shape, mapped to the view types the console renders. Cognito staff-auth (♻️ same posture as the
// concern/question consoles — handler/kora.go).

import type {
  KORAQueueRow,
  KORAStatus,
  RecordsPreferredFormat,
  RespondKORAInput,
  StaffKORADetail,
} from "./types.js";

/**
 * A KORA request IS a Request in storage, so the wire shape is the RAW Request JSON (the same
 * convention as WireQuestion): `description` holds the records-sought text, `category` the record-type
 * id, `recordsDateFrom/To` the date range — NOT friendly aliases. The staff queue row adds
 * `recordTypeLabel`, `requesterName`, `ageBusinessDays`, `overdue` alongside the embedded Request
 * (handler.ListKORAQueueHandler); the queue envelope carries `respondDays` (the SLA target) once.
 */
interface WireKORA {
  id: string;
  trackingCode: string;
  category?: string; // record-type id (Category reused, Kind=kora)
  description: string; // the "records sought" free text (Description field reused)
  recordTypeLabel?: string; // queue row sibling; detail uses top-level categoryLabel instead
  recordsDateFrom?: string;
  recordsDateTo?: string;
  preferredFormat: RecordsPreferredFormat;
  status: KORAStatus;
  assigneeId?: string | null;
  reporterContact?: ReporterContactView | null;
  requesterName?: string; // decrypted on the console queue path (or email); absent when anonymous
  denyReason?: string;
  redactionNote?: string;
  fulfilledFormat?: RecordsPreferredFormat;
  respondedAt?: string;
  ageBusinessDays?: number;
  overdue?: boolean;
  createdAt: string;
  updatedAt: string;
}

function mapKORAQueueRow(r: WireKORA, respondDays: number): KORAQueueRow {
  return {
    id: r.id,
    trackingCode: r.trackingCode,
    recordsSought: r.description,
    recordType: r.category,
    recordTypeLabel: r.recordTypeLabel,
    status: r.status,
    preferredFormat: r.preferredFormat,
    // Console handler decrypts + projects the requester NAME (or email); mocks may carry a full
    // contact. `null` when the request was anonymous (KORA allows anonymous, records-request.md).
    requesterName: r.requesterName ?? r.reporterContact?.name ?? null,
    assigneeId: r.assigneeId ?? null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    ageBusinessDays: r.ageBusinessDays ?? 0,
    overdue: r.overdue ?? false,
    targetDays: respondDays,
  };
}

/** GET /api/admin/kora — the KORA console queue (♻️ list-queue shape, Kind=kora). */
export async function listKORAQueueLive(q?: string): Promise<KORAQueueRow[]> {
  const res = await apiFetch<{
    records: WireKORA[];
    count: number;
    respondDays: number;
    citationLabel: string;
  }>("/admin/kora", { auth: true, query: { q } });
  return res.records.map((r) => mapKORAQueueRow(r, res.respondDays ?? 0));
}

interface WireKORAEvent {
  id: string;
  type: string;
  actorId?: string | null;
  source?: string;
  visibility: Visibility;
  body: string;
  toStatus?: KORAStatus;
  createdAt: string;
}

/**
 * GET /api/admin/kora/{id} — a KORA request IS a Request (same storage as concern/question), so
 * this reads the SAME shape convention as getQuestionLive: request fields nested under `request`,
 * with categoryLabel/reporterContact/events/attachments/SLA-clock fields at the TOP level.
 */
export async function getKORALive(id: string): Promise<StaffKORADetail | null> {
  // A KORA request IS a Request — hydrate from the SAME Kind-agnostic GET /requests/{id}
  // (handler.GetRequest) the concern/question consoles use: raw Request fields nested under
  // `request`, with categoryLabel/reporterContact/events/attachments at the top level. Read the
  // records-sought text off `request.description` and the record type off `request.category` (their
  // real stored fields). GetRequest additionally surfaces the KORA response clock
  // (ageBusinessDays/overdue/targetDays) at the top level for kora rows.
  let res: {
    request: WireKORA;
    categoryLabel?: string;
    reporterContact: ReporterContactView | null;
    attachments?: WireAttachment[];
    events?: WireKORAEvent[];
    ageBusinessDays?: number;
    overdue?: boolean;
    targetDays?: number;
  };
  try {
    res = await apiFetch(`/requests/${encodeURIComponent(id)}`, { auth: true });
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
  const r = res.request;
  return {
    id: r.id,
    trackingCode: r.trackingCode,
    recordsSought: r.description,
    recordType: r.category,
    recordTypeLabel: res.categoryLabel ?? r.recordTypeLabel,
    dateFrom: r.recordsDateFrom,
    dateTo: r.recordsDateTo,
    preferredFormat: r.preferredFormat,
    status: r.status,
    reporterContact: res.reporterContact ?? null,
    assigneeId: r.assigneeId ?? null,
    attachments: (res.attachments ?? []).map(mapAttachment),
    events: (res.events ?? []).map((e) => ({
      id: e.id,
      type: (e.type as StaffKORADetail["events"][number]["type"]) ?? "note",
      visibility: e.visibility,
      body: e.body,
      toStatus: e.toStatus,
      actorName: e.source === "resident" ? "Resident" : (e.actorId ?? null),
      createdAt: e.createdAt,
    })),
    denyReason: r.denyReason,
    redactionNote: r.redactionNote,
    fulfilledFormat: r.fulfilledFormat,
    respondedAt: r.respondedAt,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    ageBusinessDays: res.ageBusinessDays ?? 0,
    overdue: res.overdue ?? false,
    targetDays: res.targetDays ?? 0,
  };
}

/**
 * POST /api/admin/kora/{id}/respond — write the response (status transition + publicNote +
 * denyReason-when-denied + redactionNote-on-partial), notify the requester. Client-side transition
 * legality is validated by the caller (canTransitionKORA) before this is called; the server
 * re-validates (defense in depth, ♻️ same posture as changeStatusLive).
 */
export async function respondKORALive(id: string, input: RespondKORAInput): Promise<void> {
  await apiFetch(`/admin/kora/${encodeURIComponent(id)}/respond`, {
    method: "POST",
    auth: true,
    body: input,
  });
}

// ---- Code Enforcement (Case) requests: staff console (handler/case.go) -----------------------
//
// Mirrors the KORA console's live-mapping conventions above: WireX interfaces for the RAW Go JSON
// shape, mapped to the view types the console renders. Cognito staff-auth, STAFF-ONLY (no public
// intake — an officer opens a case, handler/case.go doc).

import type {
  AdvanceCaseInput,
  CaseQueueRow,
  CaseStatus,
  OpenCaseInput,
  OpenCaseResult,
  StaffCaseDetail,
} from "./types.js";

/**
 * A case IS a Request in storage, so the wire shape is the RAW Request JSON (same convention as
 * WireKORA/WireQuestion): `description` holds the violation description, `category` the
 * violation-type id, `ordinanceRef`/`complianceDeadline`/`cureSteps`/`ownerName`/`ownerAddress`/
 * `reinspectionOutcome`/`sourceRequestId` — NOT friendly aliases (services/api/internal/shared/
 * schemas.go Request struct, exact json tags). The staff queue row adds `violationTypeLabel`/
 * `overdue`/`daysOverdue` alongside the embedded Request (handler.ListCaseQueueHandler).
 */
interface WireCase {
  id: string;
  trackingCode: string;
  category?: string; // violation-type id (Category reused, Kind=case)
  violationTypeLabel?: string; // queue row sibling; detail uses top-level categoryLabel instead
  description: string; // violation description (Description field reused)
  status: CaseStatus;
  ordinanceRef?: string;
  complianceDeadline?: string;
  noticeIssuedAt?: string;
  cureSteps?: string;
  ownerName?: string;
  ownerAddress?: string;
  reinspectionOutcome?: string;
  sourceRequestId?: string;
  propertyId?: string | null;
  assigneeId?: string | null;
  overdue?: boolean;
  daysOverdue?: number;
  createdAt: string;
  updatedAt: string;
}

function mapCaseQueueRow(r: WireCase): CaseQueueRow {
  return {
    id: r.id,
    trackingCode: r.trackingCode,
    violationType: r.category,
    violationTypeLabel: r.violationTypeLabel,
    description: r.description,
    status: r.status,
    ownerName: r.ownerName,
    propertyId: r.propertyId ?? null,
    assigneeId: r.assigneeId ?? null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    complianceDeadline: r.complianceDeadline,
    overdue: r.overdue ?? false,
    daysOverdue: r.daysOverdue ?? 0,
  };
}

/** GET /api/admin/cases — the case console queue (♻️ list-kora shape, Kind=case). Envelope key is
 *  `cases` (handler.ListCaseQueueHandler). */
export async function listCasesLive(q?: string): Promise<CaseQueueRow[]> {
  const res = await apiFetch<{ cases: WireCase[]; count: number }>("/admin/cases", {
    auth: true,
    query: { q },
  });
  return res.cases.map(mapCaseQueueRow);
}

interface WireCaseEvent {
  id: string;
  type: string;
  actorId?: string | null;
  source?: string;
  visibility: Visibility;
  body: string;
  toStatus?: CaseStatus;
  createdAt: string;
}

/**
 * GET /api/requests/{id} — a case IS a Request (same storage as concern/question/KORA), so this
 * hydrates from the SAME Kind-agnostic endpoint the other consoles use (handler.GetRequest): raw
 * Request fields nested under `request`, with categoryLabel/reporterContact/events/attachments at
 * the top level. GetRequest additionally surfaces the compliance-deadline clock
 * (overdue/complianceDeadline/daysOverdue) at the top level for case rows (staff.go GetRequest
 * KindCase branch) — read those off the TOP level, exactly like getKORALive reads
 * ageBusinessDays/overdue/targetDays. A case's reporterContact is always empty/absent
 * (officer-opened, no public intake) so it's simply never read here.
 */
export async function getCaseLive(id: string): Promise<StaffCaseDetail | null> {
  let res: {
    request: WireCase;
    categoryLabel?: string;
    attachments?: WireAttachment[];
    events?: WireCaseEvent[];
    overdue?: boolean;
    complianceDeadline?: string;
    daysOverdue?: number;
  };
  try {
    res = await apiFetch(`/requests/${encodeURIComponent(id)}`, { auth: true });
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
  const r = res.request;
  return {
    id: r.id,
    trackingCode: r.trackingCode,
    violationType: r.category,
    violationTypeLabel: res.categoryLabel ?? r.violationTypeLabel,
    description: r.description,
    status: r.status,
    ordinanceRef: r.ordinanceRef,
    ownerName: r.ownerName,
    ownerAddress: r.ownerAddress,
    complianceDeadline: res.complianceDeadline ?? r.complianceDeadline,
    noticeIssuedAt: r.noticeIssuedAt,
    cureSteps: r.cureSteps,
    reinspectionOutcome: (r.reinspectionOutcome as StaffCaseDetail["reinspectionOutcome"]) ?? "",
    sourceRequestId: r.sourceRequestId,
    propertyId: r.propertyId ?? null,
    assigneeId: r.assigneeId ?? null,
    attachments: (res.attachments ?? []).map(mapAttachment),
    events: (res.events ?? []).map((e) => ({
      id: e.id,
      type: (e.type as StaffCaseDetail["events"][number]["type"]) ?? "note",
      visibility: e.visibility,
      body: e.body,
      toStatus: e.toStatus,
      actorName: e.source === "resident" ? "Resident" : (e.actorId ?? null),
      createdAt: e.createdAt,
    })),
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    overdue: res.overdue ?? false,
    daysOverdue: res.daysOverdue ?? 0,
  };
}

/** POST /api/admin/cases — open a new code-enforcement case (handler.OpenCase). Tenant/Source/
 *  EnteredBy/status/tracking-code are ALL server-set — never accepted here. */
export async function openCaseLive(input: OpenCaseInput): Promise<OpenCaseResult> {
  return apiFetch<OpenCaseResult>("/admin/cases", {
    method: "POST",
    auth: true,
    body: input,
  });
}

/**
 * POST /api/admin/cases/{id}/advance — move a case through its lifecycle (handler.AdvanceCase).
 * Client-side transition legality is validated by the caller (canTransitionCase) before this is
 * called; the server re-validates (defense in depth, ♻️ same posture as respondKORALive). No
 * notify call server-side — case notices are printed/mailed PDFs, not emails.
 */
export async function advanceCaseLive(id: string, input: AdvanceCaseInput): Promise<void> {
  await apiFetch(`/admin/cases/${encodeURIComponent(id)}/advance`, {
    method: "POST",
    auth: true,
    body: input,
  });
}

// ---- Permits & Licensing: staff console (handler/permit.go) -----------------------------------
//
// Mirrors the KORA console's live-mapping conventions above: WireX interfaces for the RAW Go JSON
// shape, mapped to the view types the console renders. Cognito staff-auth. UNLIKE Case, a permit
// DOES have a public intake (an applicant files their own application, like KORA) so, like KORA,
// this reads reporterContact/applicant fields.

import type {
  AdvancePermitInput,
  FormSchema,
  FormValues,
  Inspection,
  InspectionProgressSummary,
  PermitQueueRow,
  PermitStatus,
  RecordInspectionInput,
  StaffPermitDetail,
} from "./types.js";

/**
 * A permit application IS a Request in storage, so the wire shape is the RAW Request JSON (same
 * convention as WireKORA/WireCase): `description` holds the project description
 * (SubmitPermitInput.projectDescription), `category` the permit-type id, `location` the project
 * address, `conditions`/`denyReason`/`permitNumber`/`issuedAt` - NOT friendly aliases (services/api/
 * internal/shared/schemas.go Request struct, exact json tags). Fee model (permits-fidelity.md §A.5,
 * rename-with-alias): `totalFeeAmount` REPLACES the shipped `feeAmount`'s role as "the total" (the
 * on-disk DynamoDB attribute stays `feeAmount`; a legacy prod permit reads correctly with no
 * migration); `appFeeAmount`/`impactFeeAmount` are net-new (0 on a legacy permit). `formValues`/
 * `formSchemaVersion` are the applicant's schema-driven answers (permits-fidelity.md §A.2) — absent/
 * empty for a legacy free-text-only application. The staff queue row adds
 * `permitTypeLabel`/`applicantName`/`reporterName` alongside the embedded Request
 * (handler.ListPermitQueueHandler).
 */
interface WirePermit {
  id: string;
  trackingCode: string;
  category?: string; // permit-type id (Category reused, Kind=permit)
  permitTypeLabel?: string; // queue row sibling; detail uses top-level categoryLabel instead
  description: string; // project description (Description field reused)
  location: { address: string; lat?: number; lng?: number };
  status: PermitStatus;
  conditions?: string;
  denyReason?: string;
  totalFeeAmount?: number;
  appFeeAmount?: number;
  impactFeeAmount?: number;
  feePaid?: boolean;
  permitNumber?: string;
  issuedAt?: string;
  assigneeId?: string | null;
  reporterContact?: ReporterContactView | null;
  applicantName?: string; // decrypted on the console queue path (or email); absent when no contact
  reporterName?: string;
  formValues?: FormValues;
  formSchemaVersion?: number;
  createdAt: string;
  updatedAt: string;
}

function mapPermitQueueRow(r: WirePermit): PermitQueueRow {
  return {
    id: r.id,
    trackingCode: r.trackingCode,
    permitType: r.category,
    permitTypeLabel: r.permitTypeLabel,
    projectDescription: r.description,
    address: r.location?.address,
    status: r.status,
    // Console handler decrypts + projects the applicant NAME (or email); mocks may carry a full
    // contact. `null` when the applicant left no contact.
    applicantName: r.applicantName ?? r.reporterName ?? r.reporterContact?.name ?? null,
    totalFeeAmount: r.totalFeeAmount ?? 0,
    appFeeAmount: r.appFeeAmount ?? 0,
    impactFeeAmount: r.impactFeeAmount ?? 0,
    feePaid: r.feePaid ?? false,
    assigneeId: r.assigneeId ?? null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

/** GET /api/admin/permits - the permit console queue (list-kora shape, Kind=permit). Envelope
 *  key is `permits` (handler.ListPermitQueueHandler). */
export async function listPermitsLive(q?: string): Promise<PermitQueueRow[]> {
  const res = await apiFetch<{ permits: WirePermit[]; count: number }>("/admin/permits", {
    auth: true,
    query: { q },
  });
  return res.permits.map(mapPermitQueueRow);
}

interface WirePermitEvent {
  id: string;
  type: string;
  actorId?: string | null;
  source?: string;
  visibility: Visibility;
  body: string;
  toStatus?: PermitStatus;
  createdAt: string;
}

/**
 * GET /api/requests/{id} - a permit application IS a Request (same storage as concern/question/
 * KORA/case), so this hydrates from the SAME Kind-agnostic endpoint the other consoles use
 * (handler.GetRequest): raw Request fields nested under `request`, with
 * categoryLabel/reporterContact/events/attachments at the top level. A permit has no SLA clock in
 * MVP, so there are no extra top-level fields to read here (unlike KORA's ageBusinessDays/overdue
 * or Case's complianceDeadline).
 */
export async function getPermitLive(id: string): Promise<StaffPermitDetail | null> {
  let res: {
    request: WirePermit;
    categoryLabel?: string;
    reporterContact: ReporterContactView | null;
    attachments?: WireAttachment[];
    events?: WirePermitEvent[];
    // Structured Inspections (permit-inspections.md Phase 1) — present only when this request is a
    // permit (handler.GetRequest); inspectionTypes is undefined/empty for a type with no checklist.
    inspections?: Inspection[];
    inspectionTypes?: InspectionTypeDef[];
    inspectionProgress?: InspectionProgressSummary;
  };
  try {
    res = await apiFetch(`/requests/${encodeURIComponent(id)}`, { auth: true });
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
  const r = res.request;
  // The FormSchema lives on the permit-TYPE Category (permits-fidelity.md §A.1), not on the Request
  // itself — a second (cheap, cached-by-caller) read of the live permit-type taxonomy so the read
  // view can render the applicant's formValues sectioned the same way the apply form collected them.
  let formSchema: FormSchema | undefined;
  let requiredDocs: StaffPermitDetail["requiredDocs"];
  if (r.category) {
    try {
      const types = await listPermitTypesLive();
      const type = types.find((t) => t.id === r.category);
      formSchema = type?.formSchema;
      requiredDocs = type?.requiredDocs;
    } catch {
      // Non-fatal — the detail page just skips the "Application detail" section.
    }
  }
  return {
    id: r.id,
    trackingCode: r.trackingCode,
    permitType: r.category,
    permitTypeLabel: res.categoryLabel ?? r.permitTypeLabel,
    projectDescription: r.description,
    location: r.location,
    status: r.status,
    reporterContact: res.reporterContact ?? null,
    conditions: r.conditions,
    denyReason: r.denyReason,
    totalFeeAmount: r.totalFeeAmount ?? 0,
    appFeeAmount: r.appFeeAmount ?? 0,
    impactFeeAmount: r.impactFeeAmount ?? 0,
    feePaid: r.feePaid ?? false,
    permitNumber: r.permitNumber,
    issuedAt: r.issuedAt,
    assigneeId: r.assigneeId ?? null,
    attachments: (res.attachments ?? []).map(mapAttachment),
    events: (res.events ?? []).map((e) => ({
      id: e.id,
      type: (e.type as StaffPermitDetail["events"][number]["type"]) ?? "note",
      visibility: e.visibility,
      body: e.body,
      toStatus: e.toStatus,
      actorName: e.source === "resident" ? "Resident" : (e.actorId ?? null),
      createdAt: e.createdAt,
    })),
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    formSchema,
    formValues: r.formValues,
    formSchemaVersion: r.formSchemaVersion,
    requiredDocs,
    inspections: res.inspections,
    inspectionTypes: res.inspectionTypes,
    inspectionProgress: res.inspectionProgress,
  };
}

/**
 * POST /api/admin/permits/{id}/advance - move a permit through its lifecycle
 * (handler.AdvancePermit). Client-side transition legality is validated by the caller
 * (canTransitionPermit) before this is called; the server re-validates (defense in depth, same
 * posture as advanceCaseLive). UNLIKE Case, this DOES notify the applicant (the applicant has a
 * contact and IS emailed, task brief).
 */
export async function advancePermitLive(id: string, input: AdvancePermitInput): Promise<void> {
  await apiFetch(`/admin/permits/${encodeURIComponent(id)}/advance`, {
    method: "POST",
    auth: true,
    body: input,
  });
}

/**
 * POST /api/admin/permits/{id}/inspection - record a pass/fail inspection outcome
 * (handler.RecordInspection). APPENDS a public-visibility inspection event to the permit's
 * timeline - does NOT itself change the permit's status.
 */
export async function recordInspectionLive(
  id: string,
  input: RecordInspectionInput,
): Promise<void> {
  await apiFetch(`/admin/permits/${encodeURIComponent(id)}/inspection`, {
    method: "POST",
    auth: true,
    body: input,
  });
}

/** Kind=permit categories only - the STAFF-side live permit-type taxonomy (PermitDetailPage's
 *  permit type, ApplyForPermitPage's type picker, CategoriesAdmin's "Permit types" tab). Staff-auth,
 *  unfiltered by active (mirrors listCaseViolationTypes). */
export async function listPermitTypesLive(): Promise<CategoryView[]> {
  return listCategoriesLive("permit");
}

// ---- Permit → POA daily digest — "Log a paper permit" (docs/features/permit-poa-digest.md) ------

/**
 * POST /api/admin/permits/ocr — Claude OCR-reads a scanned/photographed paper permit into
 * SchemaFormRenderer-shaped FormValues + a parallel per-field confidence map. Staff-only, explicit/
 * opt-in (never automatic) — same AI-action posture as draftAgenda/summarizeAgendaItem. Throws
 * ApiError with code "ai_not_configured"/"ai_monthly_cap_reached" (503), "ai_unavailable" (502), or
 * "unsupported_file_type"/"file_too_large"/"unknown_permit_type" (400) — callers branch on `err.code`.
 */
export async function ocrPermitFieldsLive(
  input: OcrPermitFieldsInput,
): Promise<OcrPermitFieldsResult> {
  return apiFetch<OcrPermitFieldsResult>("/admin/permits/ocr", {
    method: "POST",
    auth: true,
    body: input,
  });
}

/**
 * POST /api/admin/permits — staff-counter/paper/scanned permit intake (handler.CreatePermitByStaff).
 * UNLIKE the resident SubmitPermit path, the clerk CHOOSES the initial status. Returns a thin ack
 * (not the full permit) — callers re-fetch via getPermit(id), same "mutation returns an ack, caller
 * re-GETs" convention as every other staff mutation in this client.
 */
export async function createPermitByStaffLive(
  input: CreatePermitByStaffInput,
): Promise<CreatePermitByStaffResult> {
  return apiFetch<CreatePermitByStaffResult>("/admin/permits", {
    method: "POST",
    auth: true,
    body: input,
  });
}

/**
 * POST /api/admin/subscribers — staff-added subscriber, confirmed immediately (no double opt-in
 * email). Returns the SAME SubscriberView shape ListSubscribers/UnsubscribeSubscriber use, so the
 * caller can insert the row directly.
 */
export async function addSubscriberByStaffLive(
  input: AddSubscriberByStaffInput,
): Promise<SubscriberView> {
  const res = await apiFetch<{ status: string; subscription: SubscriberView }>(
    "/admin/subscribers",
    {
      method: "POST",
      auth: true,
      body: input,
    },
  );
  return res.subscription;
}

// ---- Building & Code Enforcement Council Report (docs/features/council-report.md) -------------
//
// GET /api/admin/reports/council?scope=permits|cases|combined&from=&to=[&format=pdf] - staff-auth,
// READ-ONLY (handler.GetCouncilReport). `from`/`to` are calendar dates ("YYYY-MM-DD"), REQUIRED (no
// default range - a council report always names its period explicitly, ResolveCouncilReportRange).

export async function getCouncilReportLive(
  scope: CouncilReportScope,
  from: string,
  to: string,
): Promise<CouncilReportResult> {
  return apiFetch<CouncilReportResult>("/admin/reports/council", {
    auth: true,
    query: { scope, from, to },
  });
}

/** PDF variant of the same report (`?format=pdf`) - same auth-header + blob pattern as
 *  exportCsvLive (a plain `<a href>` can't carry the Cognito Authorization header). Returns the
 *  raw PDF Blob; the caller mints an object URL to trigger the browser download. */
export async function getCouncilReportPdfLive(
  scope: CouncilReportScope,
  from: string,
  to: string,
): Promise<Blob> {
  const params = new URLSearchParams({ scope, from, to, format: "pdf" });
  const res = await fetch(`/api/admin/reports/council?${params.toString()}`, {
    headers: authHeader(),
  });
  if (!res.ok) throw new Error(`Export failed (${res.status}).`);
  return res.blob();
}

// ---- Postal (snail-mail) updates — staff mailing-list export (docs/features/postal-updates.md) --
//
// GET /api/admin/subscribers/mailing-list.csv?channel=<id?> (Cognito staff auth) → text/csv,
// Content-Disposition: attachment. Same auth-header + blob pattern as exportCsvLive/
// getCouncilReportPdfLive (a plain `<a href>` can't carry the Cognito Authorization header). One
// row per confirmed subscriber whose deliveryMethod ∈ {mail, both} AND MailingAddress is present
// AND (no channel filter OR subscribed to that channel); columns:
// name,line1,line2,city,state,zip,email,channels,deliveryMethod.

/** Ask the server for the mailing-list CSV, optionally filtered to one channel. Returns the raw
 *  CSV Blob; the caller mints an object URL to trigger the browser download (♻️ handleExportPdf
 *  pattern, CouncilReportPage.tsx). */
export async function exportMailingListCsvLive(channel?: string): Promise<Blob> {
  const params = channel ? `?${new URLSearchParams({ channel }).toString()}` : "";
  const res = await fetch(`/api/admin/subscribers/mailing-list.csv${params}`, {
    headers: authHeader(),
  });
  if (!res.ok) throw new Error(`Export failed (${res.status}).`);
  return res.blob();
}

// ---- Subscriber self-service — staff console (docs/features/subscriber-self-service.md Part B) --
//
// GET /api/admin/subscribers?q=&status= → all-status subscriber list (search + filter, admin-gated
// PII). POST /api/admin/subscribers/{id}/unsubscribe → staff-initiated unsubscribe (audited
// source="staff" server-side; idempotent — already-unsubbed reads as success).

/** GET /api/admin/subscribers?q=<search?>&status=<filter?> — search matches email/name server-side
 *  (PII stays server-side); includes ALL statuses so staff see the full picture (wire contract
 *  pinned in subscriber-self-service.md — no drift). */
export async function listSubscribersLive(
  q?: string,
  status?: SubscriptionStatus,
): Promise<SubscriberView[]> {
  const res = await apiFetch<{ subscribers: SubscriberView[] }>("/admin/subscribers", {
    auth: true,
    query: { q, status },
  });
  return res.subscribers;
}

/** POST /api/admin/subscribers/{id}/unsubscribe — staff-initiated unsubscribe (reuses
 *  UnsubscribeSubscription; writes an EventConsentUnsubscribed audit event source="staff").
 *  Idempotent: an already-unsubscribed subscriber reads as success. */
export async function unsubscribeSubscriberLive(id: string): Promise<SubscriberView> {
  const res = await apiFetch<{ status: string; subscription: SubscriberView }>(
    `/admin/subscribers/${encodeURIComponent(id)}/unsubscribe`,
    { method: "POST", auth: true },
  );
  return res.subscription;
}

/** POST /api/admin/subscribers/{id}/resend-confirmation — staff-initiated resend of the double
 *  opt-in confirmation email to a still-"pending" subscriber. Does NOT confirm the subscriber;
 *  the row's status is unchanged (still "pending") — the returned `subscription` is echoed back
 *  for symmetry with unsubscribeSubscriberLive so callers can replace the row uniformly. */
export async function resendSubscriberConfirmationLive(id: string): Promise<SubscriberView> {
  const res = await apiFetch<{ status: string; subscription: SubscriberView }>(
    `/admin/subscribers/${encodeURIComponent(id)}/resend-confirmation`,
    { method: "POST", auth: true },
  );
  return res.subscription;
}

// ---- Council Agenda + Packet — staff builder (agenda-packet.md §10A Phase 3) ------------------
//
// Every path/body below is a byte-exact mirror of services/api/internal/handler/meetings.go's route
// table + shared.*Input JSON tags — no drift. adminAuth-gated (Cognito staff token via apiFetch's
// `auth: true`), same as every other admin-console call in this file.

import type {
  MeetingView,
  MeetingsListPage,
  MeetingBundle,
  CreateMeetingInput as MeetingCreateInput,
  UpdateMeetingInput as MeetingUpdateInput,
  PublishMeetingInput,
  AgendaItemView,
  AgendaItemInput,
  MeetingAttachmentView,
  AttachMeetingFileInput,
  AttachMeetingLinkInput,
  MinutesView,
  UpsertMinutesInput,
  ContributionView,
  AssignContributionInput,
  SaveContributionInput,
  DraftAgendaSection,
  SummarizeAgendaItemResult,
  CreateAgendaItemInput,
  ReorderAgendaItemPosition,
  TranscriptJobState,
  TranscriptJobView,
  RecordingUploadPresign,
  TranscribeMeetingInput,
  ExportPacketInput,
  ExportPacketResult,
  YouTubeStatusView,
  YouTubeAuthorizeView,
  YouTubeTranscriptPullResult,
  MeetingTemplateView,
  CreateMeetingTemplateInput,
  UpdateMeetingTemplateInput,
  MaterializeTemplateInput,
  SaveAsTemplateInput,
} from "./types.js";

/** GET /api/admin/meetings?pageSize=&cursor= — ONE PAGE of this tenant's meetings (draft +
 *  published + archived), newest-first from the server. Omit `cursor` for page 1; pass the prior
 *  page's `nextCursor` to fetch the next one. An empty `nextCursor` on the response means the last
 *  page has been reached. */
export async function listMeetingsLive(
  cursor?: string,
  pageSize?: number,
): Promise<MeetingsListPage> {
  const res = await apiFetch<MeetingsListPage>("/admin/meetings", {
    auth: true,
    query: { cursor, pageSize: pageSize != null ? String(pageSize) : undefined },
  });
  return { meetings: res.meetings ?? [], nextCursor: res.nextCursor ?? "" };
}

/** POST /api/admin/meetings — create a new draft meeting. */
export async function createMeetingLive(input: MeetingCreateInput): Promise<MeetingView> {
  const res = await apiFetch<{ meeting: MeetingView }>("/admin/meetings", {
    method: "POST",
    auth: true,
    body: input,
  });
  return res.meeting;
}

/** GET /api/admin/meetings/{id} — the full bundle (meeting + items + attachments + contributions +
 *  minutes + events). */
export async function getMeetingBundleLive(id: string): Promise<MeetingBundle | null> {
  try {
    return await apiFetch<MeetingBundle>(`/admin/meetings/${encodeURIComponent(id)}`, {
      auth: true,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** PUT /api/admin/meetings/{id} — edit a DRAFT meeting's own fields. */
export async function updateMeetingLive(
  id: string,
  input: MeetingUpdateInput,
): Promise<MeetingView> {
  const res = await apiFetch<{ meeting: MeetingView }>(
    `/admin/meetings/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      auth: true,
      body: input,
    },
  );
  return res.meeting;
}

/** DELETE /api/admin/meetings/{id} — delete a DRAFT meeting. */
export async function deleteMeetingLive(id: string): Promise<void> {
  await apiFetch(`/admin/meetings/${encodeURIComponent(id)}`, { method: "DELETE", auth: true });
}

/** POST /api/admin/meetings/{id}/publish — draft -> published (defaults agendaState to
 *  "tentative" when omitted), OR on an already-published meeting just updates agendaState (the
 *  "mark final" path, stays published) — agenda-packet.md §10A tentative/final. */
export async function publishMeetingLive(
  id: string,
  input?: PublishMeetingInput,
): Promise<MeetingView> {
  const res = await apiFetch<{ meeting: MeetingView }>(
    `/admin/meetings/${encodeURIComponent(id)}/publish`,
    { method: "POST", auth: true, body: input },
  );
  return res.meeting;
}

/** POST /api/admin/meetings/{id}/unpublish — published -> draft (a correction; audited). */
export async function unpublishMeetingLive(id: string): Promise<MeetingView> {
  const res = await apiFetch<{ meeting: MeetingView }>(
    `/admin/meetings/${encodeURIComponent(id)}/unpublish`,
    { method: "POST", auth: true },
  );
  return res.meeting;
}

/** POST /api/admin/meetings/{id}/items — add an agenda item, appended at the end of its sibling
 *  group. An optional `parentItemId` on the input creates a first-class SUB-ITEM under an existing
 *  top-level item (Council Agenda 10A) — the server enforces the two-level cap. */
export async function addAgendaItemLive(
  meetingId: string,
  input: CreateAgendaItemInput,
): Promise<AgendaItemView> {
  const res = await apiFetch<{ item: AgendaItemView }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/items`,
    { method: "POST", auth: true, body: input },
  );
  return res.item;
}

/** PUT /api/admin/meetings/{id}/items/{itemId} — edit title/narrative/presenter/outcome/vote (order
 *  is UNCHANGED here — see reorderAgendaItemsLive). */
export async function updateAgendaItemLive(
  meetingId: string,
  itemId: string,
  input: AgendaItemInput,
): Promise<AgendaItemView> {
  const res = await apiFetch<{ item: AgendaItemView }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}`,
    { method: "PUT", auth: true, body: input },
  );
  return res.item;
}

/** DELETE /api/admin/meetings/{id}/items/{itemId} — remove an agenda item. 409s
 *  `section_not_empty` if this is a top-level item (a "section") that still has sub-items pointing
 *  at it — the caller must move/remove them first (surfaced as a friendly toast, not a raw error). */
export async function deleteAgendaItemLive(meetingId: string, itemId: string): Promise<void> {
  await apiFetch(
    `/admin/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE", auth: true },
  );
}

/** POST /api/admin/meetings/{id}/items/reorder — the staff drag/keyboard TREE reorder + reparent
 *  (Council Agenda 10A). Body carries the FULL new nested ordering: every top-level item at the
 *  array root, every sub-item nested under its parent's `children` (no deeper nesting). Every
 *  current agenda item must be named exactly once across the whole payload. */
export async function reorderAgendaItemsLive(
  meetingId: string,
  items: ReorderAgendaItemPosition[],
): Promise<void> {
  await apiFetch(`/admin/meetings/${encodeURIComponent(meetingId)}/items/reorder`, {
    method: "POST",
    auth: true,
    body: { items },
  });
}

/** POST /api/admin/meetings/{id}/items/{itemId}/attachments — either register a file already
 *  uploaded via the shared presigned-doc-upload path (♻️ uploadDocumentLive, `s3Key`) OR attach an
 *  external link (`linkUrl`) as an item attachment. Pass `replaceAttachmentId` to update an EXISTING
 *  attachment in place (same route, same wire shape) instead of creating a new one. */
export async function attachMeetingFileLive(
  meetingId: string,
  itemId: string,
  input: AttachMeetingFileInput,
): Promise<MeetingAttachmentView> {
  const res = await apiFetch<{ attachment: MeetingAttachmentView }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}/attachments`,
    { method: "POST", auth: true, body: input },
  );
  return res.attachment;
}

/** POST /api/admin/meetings/{id}/attachments — a MANUAL, MEETING-LEVEL attach (no agenda item
 *  required), EITHER an external link (`linkUrl`+`linkType`, server derives the display label from
 *  `linkType`) OR a file already uploaded via `uploadDocumentLive` (`s3Key`, display label falls back
 *  to `filename`). */
export async function attachMeetingLinkLive(
  meetingId: string,
  input: AttachMeetingLinkInput,
): Promise<MeetingAttachmentView> {
  const res = await apiFetch<{ attachment: MeetingAttachmentView }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/attachments`,
    { method: "POST", auth: true, body: input },
  );
  return res.attachment;
}

/** POST .../attachments with `deleteAttachmentId` — REMOVE an attachment from an agenda item.
 *  Reuses the attach endpoint (no separate DELETE route) with a delete flag, exactly like Replace
 *  reuses it with `replaceAttachmentId`. */
export async function removeMeetingFileLive(
  meetingId: string,
  itemId: string,
  attachmentId: string,
): Promise<void> {
  await apiFetch(
    `/admin/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}/attachments`,
    { method: "POST", auth: true, body: { deleteAttachmentId: attachmentId } },
  );
}

/** PUT /api/admin/meetings/{id}/minutes — hand-author/edit a meeting's minutes; `publishRecap`/
 *  `publishMinutes` independently set Minutes.RecapPublished/MinutesPublished (never clear them —
 *  one-directional, see UpsertMinutesInput's doc). */
export async function saveMeetingMinutesLive(
  meetingId: string,
  input: UpsertMinutesInput,
): Promise<MinutesView> {
  const res = await apiFetch<{ minutes: MinutesView }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/minutes`,
    { method: "PUT", auth: true, body: input },
  );
  return res.minutes;
}

/** POST /api/admin/meetings/{id}/minutes/adopt — the clerk marks the FORMAL minutes adopted after
 *  council approval at a later meeting. One-directional (no un-adopt action in v1). */
export async function adoptMeetingMinutesLive(meetingId: string): Promise<MinutesView> {
  const res = await apiFetch<{ minutes: MinutesView }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/minutes/adopt`,
    { method: "POST", auth: true },
  );
  return res.minutes;
}

/** POST /api/admin/meetings/{id}/items/{itemId}/contributions — the clerk creates/reassigns a
 *  department report slot. */
export async function assignContributionLive(
  meetingId: string,
  itemId: string,
  input: AssignContributionInput,
): Promise<ContributionView> {
  const res = await apiFetch<{ contribution: ContributionView }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}/contributions`,
    { method: "POST", auth: true, body: input },
  );
  return res.contribution;
}

/** PUT /api/admin/meetings/{id}/items/{itemId}/contributions/{department} — the clerk writes/edits a
 *  department's report on their behalf, optionally transitioning its state (e.g. Submitted ->
 *  Included when assembling the packet). */
export async function saveContributionByClerkLive(
  meetingId: string,
  itemId: string,
  department: string,
  input: SaveContributionInput,
): Promise<ContributionView> {
  const res = await apiFetch<{ contribution: ContributionView }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}/contributions/${encodeURIComponent(department)}`,
    { method: "PUT", auth: true, body: input },
  );
  return res.contribution;
}

/** POST /api/admin/meetings/{id}/draft-agenda — AI "Draft from last meeting" (agenda-packet.md
 *  §5.1). Returns a PROPOSAL only; nothing is saved until the clerk accepts sections into real
 *  agenda items via addAgendaItemLive. Throws ApiError with code "ai_not_configured" (503) /
 *  "ai_monthly_cap_reached" (503) / "ai_unavailable" (502) — callers branch on `err.code`, same
 *  convention as cleanupAnswer. `priorTranscript` is optional — a clerk-pasted prior-meeting
 *  transcript (e.g. YouTube's "Show transcript") that sharpens Old Business / Action Items
 *  alongside the prior minutes; omitted/empty behaves exactly as before (additive-only field). */
export async function draftAgendaLive(
  meetingId: string,
  carryOverHint?: string,
  priorTranscript?: string,
): Promise<DraftAgendaSection[]> {
  const res = await apiFetch<{ sections: DraftAgendaSection[] }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/draft-agenda`,
    { method: "POST", auth: true, body: { carryOverHint, priorTranscript } },
  );
  return res.sections ?? [];
}

/** POST /api/admin/meetings/{id}/items/{itemId}/summarize — "Summarize this item from its
 *  attachments": the AI reads the clerk-selected attachments (PDFs/images/Word docs) on ONE agenda
 *  item and drafts a short narrative. Returns a PROPOSAL (sanitized HTML) plus which attachments were
 *  read / skipped; nothing is saved until the clerk edits and saves the item. Throws ApiError with
 *  code "ai_not_configured"/"ai_monthly_cap_reached" (503) / "ai_unavailable" (502) /
 *  "no_readable_attachments" (422) — callers branch on `err.code`, same convention as draftAgenda. */
export async function summarizeAgendaItemLive(
  meetingId: string,
  itemId: string,
  attachmentIds: string[],
  hint?: string,
): Promise<SummarizeAgendaItemResult> {
  return apiFetch<SummarizeAgendaItemResult>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/items/${encodeURIComponent(itemId)}/summarize`,
    { method: "POST", auth: true, body: { attachmentIds, hint } },
  );
}

// ---- Council Agenda + Packet — 10B recording->minutes/summary pipeline (agenda-packet.md §5.2) --

/** POST /api/admin/meetings/{id}/recording/upload-init — presign a direct-to-S3 PUT for a meeting
 *  recording (audio-only allowlist, shared.MaxRecordingBytes ceiling — see RECORDING_UPLOAD_ACCEPT). */
export async function presignRecordingUploadLive(
  meetingId: string,
  filename: string,
  contentType: string,
): Promise<RecordingUploadPresign> {
  return apiFetch<RecordingUploadPresign>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/recording/upload-init`,
    { method: "POST", auth: true, body: { filename, contentType } },
  );
}

/** Full recording upload: presign -> PUT the file straight to S3 -> hand back the s3Key the caller
 *  passes to transcribeMeetingLive. Mirrors uploadDocumentLive's shape (♻️ same presigned-PUT path). */
export async function uploadRecordingLive(
  meetingId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  const contentType = file.type || "audio/mpeg";
  const presign = await presignRecordingUploadLive(meetingId, file.name, contentType);
  await putToPresignedUrl(presign.uploadUrl, file, contentType, onProgress);
  return presign.s3Key;
}

/** POST /api/admin/meetings/{id}/transcribe — kick off the async pipeline, EITHER from a
 *  previously-uploaded recording (`{recordingS3Key}`, the fallback path) OR a pasted transcript
 *  (`{transcript}`, the PRIMARY path — Linn Valley pastes the YouTube auto-transcript). 202 accepted
 *  (async); throws ApiError 409 "transcription_in_progress" (a job is already transcribing/drafting)
 *  or 409 "already_transcribed" (a `ready` job exists — re-submitting would overwrite finished,
 *  possibly clerk-edited minutes; callers show a friendly message, not a raw error, and never
 *  auto-retry). */
export async function transcribeMeetingLive(
  meetingId: string,
  input: TranscribeMeetingInput,
): Promise<{ state: TranscriptJobState }> {
  return apiFetch<{ state: TranscriptJobState }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/transcribe`,
    { method: "POST", auth: true, body: input },
  );
}

/** GET /api/admin/meetings/{id}/transcribe/status — poll target for the pipeline state machine
 *  (queued -> transcribing -> drafting -> ready | failed). state:"" = no recording provided yet
 *  (never a 404 — a normal, common state, not an error). */
export async function getTranscribeStatusLive(meetingId: string): Promise<TranscriptJobView> {
  return apiFetch<TranscriptJobView>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/transcribe/status`,
    { auth: true },
  );
}

/** POST /api/admin/meetings/{id}/transcribe/retry — re-enqueue a FAILED job. 409 "job_not_failed" if
 *  the job isn't currently failed (retry is the failed-state recovery action only). */
export async function retryTranscribeLive(
  meetingId: string,
): Promise<{ state: TranscriptJobState }> {
  return apiFetch<{ state: TranscriptJobState }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/transcribe/retry`,
    { method: "POST", auth: true },
  );
}

// Custom-vocabulary admin (getTranscribeVocabularyLive/saveTranscribeVocabularyLive) was removed
// from the frontend — Linn Valley drafts minutes from a pasted YouTube transcript, not AWS
// Transcribe, so tuning its custom vocabulary is moot here. The backend endpoint stays dormant.

// ---- YouTube transcript integration (2026-08-21 reversal) — OAuth connect/status/disconnect +
// per-meeting caption pull. Mirrors handler.youtube_oauth.go + handler.meeting_youtube_transcript.go.

/** GET /api/admin/youtube/connect (staff auth) — mints Google's authorize URL. The CALLER does the
 *  browser redirect (`window.location.href = authorizeUrl`); this is data for the SPA to act on,
 *  not itself a navigation. */
export async function getYouTubeAuthorizeUrlLive(): Promise<YouTubeAuthorizeView> {
  return apiFetch<YouTubeAuthorizeView>("/admin/youtube/connect", { auth: true });
}

/** GET /api/admin/youtube/status (staff auth) — whether this tenant has a live YouTube connection. */
export async function getYouTubeStatusLive(): Promise<YouTubeStatusView> {
  return apiFetch<YouTubeStatusView>("/admin/youtube/status", { auth: true });
}

/** DELETE /api/admin/youtube/disconnect (admin auth). */
export async function disconnectYouTubeLive(): Promise<YouTubeStatusView> {
  return apiFetch<YouTubeStatusView>("/admin/youtube/disconnect", { method: "DELETE", auth: true });
}

/** POST /api/admin/meetings/{id}/youtube-transcript (staff auth) — pulls the meeting's own YouTube
 *  video's official captions and stages them into the SAME drafting slot a pasted transcript lands
 *  in (RecordingSection's existing poll/minutes-review flow picks it up with no changes). Throws
 *  ApiError with `.code` one of the YouTubeTranscriptErrorCode values — callers branch on it for a
 *  specific inline message (409 "youtube_not_connected" / 422 "no_caption_tracks" / 502
 *  "asr_download_failed" are each distinct, per the task brief). */
export async function pullYouTubeTranscriptLive(
  meetingId: string,
): Promise<YouTubeTranscriptPullResult> {
  return apiFetch<YouTubeTranscriptPullResult>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/youtube-transcript`,
    { method: "POST", auth: true },
  );
}

/** POST /api/admin/meetings/{id}/packet — "Export packet": hand the client-generated agenda (+
 *  "Meeting materials" index) PDF up as base64; the server merges it with every attachment it can
 *  fetch (uploaded files always; external links only when reachable server-side) and returns a
 *  presigned GET for the assembled packet plus an included/skipped manifest. The merge can take a
 *  few seconds — no special handling needed here, `apiFetch` already brackets it with the global
 *  progress bar (docs/design/loading-feedback.md); the caller adds a button `aria-busy` state. */
export async function exportMeetingPacketLive(
  meetingId: string,
  input: ExportPacketInput,
): Promise<ExportPacketResult> {
  return apiFetch<ExportPacketResult>(`/admin/meetings/${encodeURIComponent(meetingId)}/packet`, {
    method: "POST",
    auth: true,
    body: input,
  });
}

// ---- Meeting Templates (ADR-0021, docs/features/meeting-templates.md) — staff console ----------
//
// Reusable agenda skeletons the clerk materializes into draft meetings. Byte-exact mirror of
// services/api/internal/handler/meeting_templates.go's route table + shared.*Input JSON tags.
// ADMIN-ONLY (adminAuth-gated, same as every other admin-console call in this file).

/** GET /api/admin/meeting-templates — list every template in the tenant (sections included, for
 *  the editor). */
export async function listMeetingTemplatesLive(): Promise<MeetingTemplateView[]> {
  const res = await apiFetch<{ templates: MeetingTemplateView[] }>("/admin/meeting-templates", {
    auth: true,
  });
  return res.templates ?? [];
}

/** POST /api/admin/meeting-templates — create a new template. */
export async function createMeetingTemplateLive(
  input: CreateMeetingTemplateInput,
): Promise<MeetingTemplateView> {
  const res = await apiFetch<{ template: MeetingTemplateView }>("/admin/meeting-templates", {
    method: "POST",
    auth: true,
    body: input,
  });
  return res.template;
}

/** GET /api/admin/meeting-templates/{templateId} — read one. A cross-tenant or unknown id 404s
 *  (surfaced here as `null`, mirroring getMeetingBundleLive's own 404-as-null convention). */
export async function getMeetingTemplateLive(
  templateId: string,
): Promise<MeetingTemplateView | null> {
  try {
    const res = await apiFetch<{ template: MeetingTemplateView }>(
      `/admin/meeting-templates/${encodeURIComponent(templateId)}`,
      { auth: true },
    );
    return res.template;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** PUT /api/admin/meeting-templates/{templateId} — full replace of the template's editable fields
 *  (name + metadata + sections). */
export async function updateMeetingTemplateLive(
  templateId: string,
  input: UpdateMeetingTemplateInput,
): Promise<MeetingTemplateView> {
  const res = await apiFetch<{ template: MeetingTemplateView }>(
    `/admin/meeting-templates/${encodeURIComponent(templateId)}`,
    { method: "PUT", auth: true, body: input },
  );
  return res.template;
}

/** DELETE /api/admin/meeting-templates/{templateId} — delete. Returns 200 `{status:"deleted"}`
 *  (this codebase has no bare-204 response anywhere — see the handler's own doc comment). */
export async function deleteMeetingTemplateLive(templateId: string): Promise<void> {
  await apiFetch(`/admin/meeting-templates/${encodeURIComponent(templateId)}`, {
    method: "DELETE",
    auth: true,
  });
}

/** POST /api/admin/meeting-templates/{templateId}/materialize — create a fresh DRAFT meeting from
 *  the template; the console redirects into the normal builder at the returned meeting's id. */
export async function materializeMeetingTemplateLive(
  templateId: string,
  input: MaterializeTemplateInput,
): Promise<MeetingView> {
  const res = await apiFetch<{ meeting: MeetingView }>(
    `/admin/meeting-templates/${encodeURIComponent(templateId)}/materialize`,
    { method: "POST", auth: true, body: input },
  );
  return res.meeting;
}

/** POST /api/admin/meetings/{id}/save-as-template — capture an existing meeting's structure
 *  (draft or published) as a new template. */
export async function saveMeetingAsTemplateLive(
  meetingId: string,
  input: SaveAsTemplateInput,
): Promise<MeetingTemplateView> {
  const res = await apiFetch<{ template: MeetingTemplateView }>(
    `/admin/meetings/${encodeURIComponent(meetingId)}/save-as-template`,
    { method: "POST", auth: true, body: input },
  );
  return res.template;
}

// ---- Council Meetings historical importer — staff console (meetings-embed-and-import.md §2) ----
//
// Point at a source URL -> AI-extract -> mandatory human-review gate -> explicit confirm -> commit
// (§2.1). Screens 1-3 operate entirely off the in-memory MeetingImportDetail the polling GET
// returns; nothing writes to the database until commitMeetingImportLive (§2.3 rule #1). Route paths
// match Inky's pinned contract exactly.

import type {
  CommitMeetingImportResult,
  MeetingImportDetail,
  MeetingImportRow,
  StartMeetingImportInput,
  StartMeetingImportResult,
  UpdateMeetingImportRowInput,
} from "./types.js";

/** POST /api/admin/meetings/import — kicks off the async fetch+extract job (screen 1 submit). */
export async function startMeetingImportLive(
  input: StartMeetingImportInput,
): Promise<StartMeetingImportResult> {
  return apiFetch<StartMeetingImportResult>("/admin/meetings/import", {
    method: "POST",
    auth: true,
    body: input,
  });
}

/** GET /api/admin/meetings/import/{importId} — the poll target for screen 2 (extracting/failed) and
 *  the full data source for screen 3's review table (status "ready"). */
export async function getMeetingImportLive(importId: string): Promise<MeetingImportDetail> {
  return apiFetch<MeetingImportDetail>(`/admin/meetings/import/${encodeURIComponent(importId)}`, {
    auth: true,
  });
}

/** PATCH /api/admin/meetings/import/{importId}/rows/{rowId} — the review table's inline
 *  date/body edit and include/exclude toggle (§2.2 screen 3). Returns the row's fresh state. */
export async function updateMeetingImportRowLive(
  importId: string,
  rowId: string,
  input: UpdateMeetingImportRowInput,
): Promise<MeetingImportRow> {
  return apiFetch<MeetingImportRow>(
    `/admin/meetings/import/${encodeURIComponent(importId)}/rows/${encodeURIComponent(rowId)}`,
    { method: "PATCH", auth: true, body: input },
  );
}

/** POST /api/admin/meetings/import/{importId}/commit — THE write. Everything staged above this
 *  point is in-memory only (§2.3 rule #1); this call is the first thing that touches the database. */
export async function commitMeetingImportLive(
  importId: string,
): Promise<CommitMeetingImportResult> {
  return apiFetch<CommitMeetingImportResult>(
    `/admin/meetings/import/${encodeURIComponent(importId)}/commit`,
    { method: "POST", auth: true },
  );
}

// ---- Ordinance Library — Phase B4 staff console (ordinance-library-phaseB.md §1.3/§1.5) --------
//
// Ingest (upload -> presign -> ingest) -> Structuring (poll the draft-ingest result) -> Review gate
// (mark sections reviewed / warnings addressed, driven off the SAME publishable/blockedReason read
// the server's publish gate uses) -> Publish -> Version history. Every route is staff MFA
// (adminAuth) — mirrors the B3 handler doc comments in
// services/api/internal/handler/ordinance_publish.go / ordinance_draft_ingest.go /
// ordinance_ingest_upload.go exactly (route paths, request/response shapes).

import type {
  OrdinanceCodeVersion,
  OrdinanceDraftIngestResult,
  OrdinancePublishResult,
  OrdinanceReviewProgress,
  OrdinanceVersionDetail,
} from "./types.js";

/** POST /api/admin/ordinances/upload-presign — presign a direct-to-S3 PUT for a codifier's
 *  .docx/.pdf export into the private ingest bucket. Mirrors AdminDocUpload's presign shape. */
export async function presignOrdinanceUploadLive(
  filename: string,
  contentType: string,
): Promise<{ uploadUrl: string; s3Key: string }> {
  return apiFetch<{ uploadUrl: string; s3Key: string }>("/admin/ordinances/upload-presign", {
    method: "POST",
    auth: true,
    body: { filename, contentType },
  });
}

/** Full upload: presign -> PUT the file straight to S3 -> return the s3Key to pass to
 *  draftIngestOrdinanceLive. Mirrors uploadDocumentLive/uploadRecordingLive's shape. */
export async function uploadOrdinanceFileLive(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  const presign = await presignOrdinanceUploadLive(file.name, file.type);
  await putToPresignedUrl(presign.uploadUrl, file, file.type, onProgress);
  return presign.s3Key;
}

/** POST /api/admin/ordinances/ingest — run the format-appropriate extractor and persist a new draft
 *  CodeVersion. EITHER {s3Key, filename, contentType} (an uploaded export) OR {citycode: true} (the
 *  re-pull path). Never publishes. */
export async function draftIngestOrdinanceLive(
  input:
    | { s3Key: string; filename: string; contentType: string; label?: string }
    | { citycode: true; label?: string },
): Promise<OrdinanceDraftIngestResult> {
  return apiFetch<OrdinanceDraftIngestResult>("/admin/ordinances/ingest", {
    method: "POST",
    auth: true,
    body: input,
  });
}

/** GET /api/admin/ordinances — every CodeVersion for the tenant (version-history screen). */
export async function listOrdinanceVersionsLive(): Promise<OrdinanceCodeVersion[]> {
  const res = await apiFetch<{ versions: OrdinanceCodeVersion[] }>("/admin/ordinances", {
    auth: true,
  });
  return res.versions;
}

/** GET /api/admin/ordinances/{versionId} — one version + its full node tree + the SAME
 *  publishable/blockedReason read AdminOrdinancePublish's gate check uses (preview/review screen). */
export async function getOrdinanceVersionLive(versionId: string): Promise<OrdinanceVersionDetail> {
  return apiFetch<OrdinanceVersionDetail>(`/admin/ordinances/${encodeURIComponent(versionId)}`, {
    auth: true,
  });
}

/** POST /api/admin/ordinances/{versionId}/review/sections — mark one section reviewed (Sue's
 *  per-section "Mark reviewed" affordance). Idempotent. Returns the version's fresh review state. */
export async function markOrdinanceSectionReviewedLive(
  versionId: string,
  permalink: string,
): Promise<OrdinanceReviewProgress> {
  return apiFetch<OrdinanceReviewProgress>(
    `/admin/ordinances/${encodeURIComponent(versionId)}/review/sections`,
    { method: "POST", auth: true, body: { permalink } },
  );
}

/** POST /api/admin/ordinances/{versionId}/review/warnings — mark one parse warning addressed.
 *  `warningIndex` is the note's position in the draft's parseNotes. Idempotent. */
export async function addressOrdinanceWarningLive(
  versionId: string,
  warningIndex: number,
): Promise<OrdinanceReviewProgress> {
  return apiFetch<OrdinanceReviewProgress>(
    `/admin/ordinances/${encodeURIComponent(versionId)}/review/warnings`,
    { method: "POST", auth: true, body: { warningIndex } },
  );
}

/** POST /api/admin/ordinances/{versionId}/publish — THE gate: 409 "not_publishable" unless every
 *  changed section is reviewed and every blocking warning is addressed. No client-side bypass is
 *  possible — the button-disabled state below is a UX courtesy, not the actual control
 *  (ADR-0014 §1.5, "enforced server-side, not just in the UI"). */
export async function publishOrdinanceVersionLive(
  versionId: string,
): Promise<OrdinancePublishResult> {
  return apiFetch<OrdinancePublishResult>(
    `/admin/ordinances/${encodeURIComponent(versionId)}/publish`,
    { method: "POST", auth: true },
  );
}

// ---- Ordinance Library — R3 cross-module reference layer -----------------------------------
//
// Attach/detach/list a Request-or-FAQ's structured ordinance § citations, plus the staff reverse
// lookup ("Cited by" panel) and the public search/ask surface the attach picker reuses to find a
// section. Route paths mirror the task dispatch's API contract exactly.

import type {
  OrdinanceRatingsSummary,
  OrdinanceRef,
  OrdinanceRefEntityType,
  OrdinanceRefsReverseResult,
  OrdinanceSearchHit,
} from "./types.js";

/** POST /api/admin/{entityType}/{id}/ordinance-refs — attach one § to a Request/FAQ. 409
 *  "already_attached" (dupe) / 400 "unknown_permalink" (not a live section) / 400
 *  "validation_error" (over the 20-ref cap) surface as ApiError for the caller to handle. */
export async function attachOrdinanceRefLive(
  entityType: OrdinanceRefEntityType,
  entityId: string,
  permalink: string,
  sectionLabel: string,
): Promise<OrdinanceRef> {
  const res = await apiFetch<{ ref: OrdinanceRef }>(
    `/admin/${entityType}/${encodeURIComponent(entityId)}/ordinance-refs`,
    { method: "POST", auth: true, body: { permalink, sectionLabel } },
  );
  return res.ref;
}

/** DELETE /api/admin/{entityType}/{id}/ordinance-refs/{permalink} — detach one §. */
export async function detachOrdinanceRefLive(
  entityType: OrdinanceRefEntityType,
  entityId: string,
  permalink: string,
): Promise<void> {
  await apiFetch<{ removed: boolean }>(
    `/admin/${entityType}/${encodeURIComponent(entityId)}/ordinance-refs/${encodeURIComponent(permalink)}`,
    { method: "DELETE", auth: true },
  );
}

/** GET /api/admin/{entityType}/{id}/ordinance-refs — one entity's currently-attached §s (chip
 *  list source of truth on load; attach/detach responses reconcile it optimistically after). */
export async function listOrdinanceRefsLive(
  entityType: OrdinanceRefEntityType,
  entityId: string,
): Promise<OrdinanceRef[]> {
  const res = await apiFetch<{ refs: OrdinanceRef[] }>(
    `/admin/${entityType}/${encodeURIComponent(entityId)}/ordinance-refs`,
    { auth: true },
  );
  return res.refs;
}

/** GET /api/admin/ordinance/refs?permalink=… — staff reverse lookup (has entityId — the "Cited
 *  by" panel next to a live/draft §, staff-only visibility). */
export async function getOrdinanceRefsReverseLive(
  permalink: string,
): Promise<OrdinanceRefsReverseResult> {
  return apiFetch<OrdinanceRefsReverseResult>("/admin/ordinance/refs", {
    auth: true,
    query: { permalink },
  });
}

/** GET /api/admin/ordinance/ratings — "Answer quality" panel on the Ordinance Library console:
 *  the overall 👍/👎 tally on Ask-the-code answers (rated via the public "Was this helpful?"
 *  control, ordinance-ask.js POSTing to /ordinance/ask/rate) plus the most recent downvoted
 *  questions for a clerk to spot-check. */
export async function getOrdinanceRatingsLive(): Promise<OrdinanceRatingsSummary> {
  return apiFetch<OrdinanceRatingsSummary>("/admin/ordinance/ratings", { auth: true });
}

/** POST /api/public/ordinance/ask (no auth) — the SAME grounded search/ask surface the public
 *  "/code" viewer's question-first hero calls (ordinance_ask.go), reused here as the attach
 *  picker's "search a question or a section number" step. Returns the closest-matching, always-
 *  cited officialText[] candidates regardless of whether the model was confident — good enough
 *  for a staff member to browse and pick from either way. */
export async function searchOrdinanceSectionsLive(query: string): Promise<OrdinanceSearchHit[]> {
  const res = await apiFetch<{ officialText: OrdinanceSearchHit[] }>("/public/ordinance/ask", {
    method: "POST",
    body: { query },
  });
  return res.officialText;
}

// ---- Users & Permissions (ADR-0016 §5, RBAC Phase 3) -----------------------
//
// Group-management API — ONE Lambda dispatching by path/method (cmd/admin-groups):
//   GET    /api/admin/permissions/registry   -> users:read
//   GET    /api/admin/groups                 -> users:read
//   POST   /api/admin/groups                 -> users:write
//   PATCH  /api/admin/groups/{id}             -> users:write
//   DELETE /api/admin/groups/{id}             -> users:delete
//   PUT    /api/admin/staff/{id}/groups       -> users:write
//   GET    /api/admin/me/permissions          -> identity-only (no permission required)

interface WireGroup {
  id: string;
  name: string;
  description?: string;
  permissions?: string[];
  system: boolean;
}

function mapGroup(g: WireGroup): GroupView {
  return {
    id: g.id,
    name: g.name,
    description: g.description ?? "",
    permissions: g.permissions ?? [],
    system: g.system,
  };
}

/** GET /api/admin/permissions/registry — the module×action taxonomy the permission matrix is
 *  built from (ADR-0016 §1); the SINGLE source of truth, never hand-duplicated here. */
export async function getPermissionsRegistryLive(): Promise<PermissionsRegistryModule[]> {
  const res = await apiFetch<{ registry: PermissionsRegistryModule[] }>(
    "/admin/permissions/registry",
    { auth: true },
  );
  return res.registry;
}

/** GET /api/admin/groups — every group for the tenant (shipped templates + town-created). */
export async function listGroupsLive(): Promise<GroupView[]> {
  const res = await apiFetch<{ groups: WireGroup[] }>("/admin/groups", { auth: true });
  return res.groups.map(mapGroup);
}

/** POST /api/admin/groups — create a new group. */
export async function createGroupLive(
  name: string,
  description: string,
  permissions: string[],
): Promise<GroupView> {
  const res = await apiFetch<{ group: WireGroup }>("/admin/groups", {
    method: "POST",
    auth: true,
    body: { name, description, permissions },
  });
  return mapGroup(res.group);
}

/** PATCH /api/admin/groups/{id} — edit a group's name/description/permissions. Rejects (400
 *  system_group_permissions_protected) narrowing the Admin group's grants below "every
 *  permission" — surfaced as ApiError so the editor can show the guardrail message inline. */
export async function updateGroupLive(
  id: string,
  name: string,
  description: string,
  permissions: string[],
): Promise<GroupView> {
  const res = await apiFetch<{ group: WireGroup }>(`/admin/groups/${encodeURIComponent(id)}`, {
    method: "PATCH",
    auth: true,
    body: { name, description, permissions },
  });
  return mapGroup(res.group);
}

/** DELETE /api/admin/groups/{id} — rejects (400 system_group_protected /
 *  would_lock_out_users_write, see lockoutMessage) deleting the Admin group or a group whose
 *  removal would drop the last users:write holder. */
export async function deleteGroupLive(id: string): Promise<void> {
  await apiFetch(`/admin/groups/${encodeURIComponent(id)}`, { method: "DELETE", auth: true });
}

/** PUT /api/admin/staff/{id}/groups — replace a staff user's ENTIRE group membership list (not
 *  append/remove). Rejects the same last-users:write / self-lockout guardrails as group delete. */
export async function setStaffGroupsLive(
  staffId: string,
  groupIds: string[],
): Promise<GroupView[]> {
  await apiFetch(`/admin/staff/${encodeURIComponent(staffId)}/groups`, {
    method: "PUT",
    auth: true,
    body: { groupIds },
  });
  return listGroupsLive();
}

/** GET /api/admin/me/permissions — the CALLING staff user's own effective permissions
 *  ("module:action" strings), IDENTITY-ONLY (no users:read required) — the SPA's own-UI
 *  self-gate (usePermissions hook, ADR-0016 §5). */
export async function getMyPermissionsLive(): Promise<string[]> {
  const res = await apiFetch<{ permissions: string[] }>("/admin/me/permissions", { auth: true });
  return res.permissions;
}

// ---- STAFF concerns map (staff-concerns-map.md) ----------------------------------------------

interface WireStaffConcernPin {
  id: string;
  trackingCode: string;
  category: string;
  categoryId?: string;
  type?: string;
  status: RequestStatus;
  lat: number;
  lng: number;
  location?: string;
  address?: string;
  staffOnly?: boolean;
  assignee?: string;
  lineId?: string;
  externalRef?: string;
  dateReported: string;
  dateResolved: string | null;
}

/** GET /api/requests/map — Cognito-authed (same /api/requests* tree as listQueueLive; this repo has
 *  no separate /api/staff/* tree), concerns:read-gated server-side (RequirePermission ModuleConcerns
 *  ActionRead — the SAME permission the concern queue itself requires; this client call never
 *  second-guesses that). Exact, un-fuzzed coordinates + ALL located concerns incl. staffOnly
 *  categories — the internal counterpart to the public /api/public/concerns feed. `status` on the
 *  wire is the RAW INTERNAL RequestStatus ("New"/"Acknowledged"/"InProgress"/...), never the
 *  public feed's collapsed 3-way scope. */
export async function listStaffConcernsLive(
  filter: StaffConcernsFilter = {},
): Promise<StaffConcernsView> {
  const res = await apiFetch<{ pins: WireStaffConcernPin[] }>("/requests/map", {
    auth: true,
    query: {
      category: filter.category,
      status: filter.status,
      from: filter.from,
      to: filter.to,
    },
  });
  return {
    pins: (res.pins ?? []).map((p) => ({
      id: p.id,
      trackingCode: p.trackingCode,
      category: p.category,
      categoryId: p.categoryId,
      type: p.type,
      status: p.status,
      lat: p.lat,
      lng: p.lng,
      location: p.location,
      address: p.address,
      staffOnly: p.staffOnly ?? false,
      assignee: p.assignee,
      lineId: p.lineId,
      externalRef: p.externalRef,
      dateReported: p.dateReported,
      dateResolved: p.dateResolved ?? null,
    })),
  };
}

// ---- Contractor Registry: staff console (docs/design/contractor-registry/spec.md §3a/§3b/§3d) ----
//
// GET/POST /api/admin/contractors, GET/PUT /api/admin/contractors/{id}, POST .../renew,
// POST .../notes — RBAC-gated server-side on perms.ModuleContractors (handler.AdminContractors et
// al.), NOT admin-group gated (any staff member holding the Contractors module permission reaches
// these, mirroring Permits' own posture). Trades (Kind=KindTrade Category rows) are fetched via the
// SAME /admin/categories endpoint every other taxonomy uses (listCategoriesLive("trade")) — no new
// backend route, per §2.1's "mirror how concern categories are modeled/seeded" directive.

export async function listContractorsLive(): Promise<ContractorQueueRow[]> {
  const res = await apiFetch<{ contractors: ContractorQueueRow[]; count: number }>(
    "/admin/contractors",
    { auth: true },
  );
  return (res.contractors ?? []).map((row) => ({
    contractor: { ...row.contractor, trades: row.contractor.trades ?? [] },
    effectiveStatus: row.effectiveStatus,
  }));
}

export async function getContractorLive(id: string): Promise<ContractorDetailView | null> {
  try {
    const res = await apiFetch<ContractorDetailView>(
      `/admin/contractors/${encodeURIComponent(id)}`,
      { auth: true },
    );
    return {
      contractor: { ...res.contractor, trades: res.contractor.trades ?? [] },
      effectiveStatus: res.effectiveStatus,
      permits: res.permits ?? [],
    };
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
}

/** POST (create, id="") or PUT (update, id set) — mirrors putContractor's upsert convention
 *  server-side. Returns the saved Contractor; callers that need the full detail shape (incl.
 *  effectiveStatus/permits) re-GET via getContractorLive, same "mutation → thin result, caller
 *  re-fetches for the full view" convention as the rest of this client. */
export async function putContractorLive(
  input: PutContractorInput,
  id?: string,
): Promise<ContractorView> {
  const res = await apiFetch<{ contractor: ContractorView }>(
    id ? `/admin/contractors/${encodeURIComponent(id)}` : "/admin/contractors",
    { method: id ? "PUT" : "POST", auth: true, body: input },
  );
  return { ...res.contractor, trades: res.contractor.trades ?? [] };
}

/** POST /api/admin/contractors/{id}/renew (spec §3b "Renew insurance cert"). */
export async function renewContractorLive(
  id: string,
  input: RenewContractorInput,
): Promise<ContractorView> {
  const res = await apiFetch<{ contractor: ContractorView }>(
    `/admin/contractors/${encodeURIComponent(id)}/renew`,
    { method: "POST", auth: true, body: input },
  );
  return { ...res.contractor, trades: res.contractor.trades ?? [] };
}

/** POST /api/admin/contractors/{id}/notes — append-only internal note log (spec §3b). Returns a
 *  thin ack (id/createdAt); callers re-GET via getContractorLive for the updated Notes blob. */
export async function addContractorNoteLive(
  id: string,
  body: string,
): Promise<{ id: string; createdAt: string }> {
  return apiFetch(`/admin/contractors/${encodeURIComponent(id)}/notes`, {
    method: "POST",
    auth: true,
    body: { body },
  });
}

/** Kind=trade categories only — the tenant-configurable trade taxonomy (spec §2.1), staff-auth,
 *  unfiltered by active (♻️ mirrors listCaseViolationTypes/listPermitTypes — an admin/compose
 *  screen still needs to resolve a deactivated trade an existing contractor record references). */
export async function listTradeCategoriesLive(): Promise<CategoryView[]> {
  return listCategoriesLive("trade");
}

// ---- B&G new-items disclosure gate: staff console (issue #489) -------------------------------
//
// Built against the staff handler contract Inky is landing in parallel (SaveBGShareApprovalConsole
// task brief) — GET /api/staff/bg-share/candidates, PUT/DELETE /api/staff/bg-share/{requestId}. This
// is the ONE place the clerk's approve/redact decision is saved; the stored shared.BGSharePayload it
// writes is the ONLY source cmd/export-new-water-items reads for a concern's outbound description —
// the raw live description is shown here for reference only and never sent on PUT. NOTE: every other
// staff endpoint in this client lives under the flat /api/requests* or /api/admin/* tree (no separate
// /api/staff/* prefix exists elsewhere in this codebase — see listStaffConcernsLive's doc comment);
// these three calls use the /api/staff/bg-share/* paths from the task's contract as given. If Inky's
// landed handler mounts them elsewhere, only the path strings below need to change.

interface WireBGSharePhoto {
  id: string;
  previewUrl: string;
}

interface WireBGShareApproval {
  approved: boolean;
  approvedBy?: string;
  approvedAt?: string;
  approvedDescription?: string;
  approvedPhotoIds?: string[];
  stale?: boolean;
}

interface WireBGShareCandidate {
  requestId: string;
  trackingCode: string;
  address: string;
  description: string;
  dateOpened: string;
  photos?: WireBGSharePhoto[];
  approval?: WireBGShareApproval;
}

function mapBGSharePhoto(p: WireBGSharePhoto): BGSharePhotoView {
  return { id: p.id, previewUrl: p.previewUrl };
}

/** A candidate with no approval saved yet reads as "not approved, nothing redacted yet" — the
 *  console defaults the edit textarea from the LIVE description client-side in that case, never from
 *  this (empty) field. */
function mapBGShareApproval(a: WireBGShareApproval | undefined): BGShareApprovalView {
  return {
    approved: a?.approved ?? false,
    approvedBy: a?.approvedBy,
    approvedAt: a?.approvedAt,
    approvedDescription: a?.approvedDescription ?? "",
    approvedPhotoIds: a?.approvedPhotoIds ?? [],
    stale: a?.stale ?? false,
  };
}

function mapBGShareCandidate(c: WireBGShareCandidate): BGShareCandidateView {
  return {
    requestId: c.requestId,
    trackingCode: c.trackingCode,
    address: c.address,
    description: c.description,
    dateOpened: c.dateOpened,
    photos: (c.photos ?? []).map(mapBGSharePhoto),
    approval: mapBGShareApproval(c.approval),
  };
}

/** GET /api/staff/bg-share/candidates — every new (ExternalRef=="") water concern awaiting (or
 *  already carrying) a clerk's B&G share review. Never carries reporter name/email/phone — this
 *  console must not render fields this endpoint doesn't return. */
export async function listBGShareCandidatesLive(): Promise<BGShareCandidateView[]> {
  const res = await apiFetch<{ candidates: WireBGShareCandidate[] }>("/staff/bg-share/candidates", {
    auth: true,
  });
  return (res.candidates ?? []).map(mapBGShareCandidate);
}

/** PUT /api/staff/bg-share/{requestId} — save the clerk's redacted description + per-photo approval
 *  and flip the gate to Approved=true (shared.BGSharePayload). Returns the saved approval so the
 *  console can reconcile (e.g. a fresh approvedAt/approvedBy, stale cleared) without a full re-GET. */
export async function saveBGShareApprovalLive(
  requestId: string,
  input: SaveBGShareApprovalInput,
): Promise<BGShareApprovalView> {
  const res = await apiFetch<{ approval: WireBGShareApproval }>(
    `/staff/bg-share/${encodeURIComponent(requestId)}`,
    { method: "PUT", auth: true, body: input },
  );
  return mapBGShareApproval(res.approval);
}

/** DELETE /api/staff/bg-share/{requestId} — unapprove: flips Approved=false so the concern drops
 *  back out of the next cmd/export-new-water-items run until a clerk re-reviews it. Does not delete
 *  the stored redacted text/photo selections — a re-approve can reuse them as the starting point. */
export async function unapproveBGShareLive(requestId: string): Promise<void> {
  await apiFetch(`/staff/bg-share/${encodeURIComponent(requestId)}`, {
    method: "DELETE",
    auth: true,
  });
}

// ---- Coaching state (GET/PUT/DELETE /api/coaching-state) — coaching framework, issue #501 -----
//
// PRIVATE per staff user, mirrors saved-views' scoping EXACTLY: the server always resolves the
// owning user from the Cognito sub (never a body-supplied owner). ONE singleton record per user
// (#504) — `{ state, checklist, updatedAt }` — NOT one item per coaching key. PUT sends
// `merge: true` explicitly so only the named `state`/`checklist` keys are patched, matching the
// "never re-fire" contract: writing one key must not accidentally clear another's status. There is
// NO per-key DELETE route — DELETE resets the WHOLE record (`{ reset: true }`); the "?" replay
// affordance never calls it (see coaching/useCoachingState.ts's `replay`).

/** GET /api/coaching-state — the caller's whole coaching record. Called once at console mount by
 *  `useCoachingState` (coaching/useCoachingState.ts). */
export async function getCoachingStateLive(): Promise<CoachingState> {
  return apiFetch<CoachingState>("/coaching-state", { auth: true });
}

/** PUT /api/coaching-state — MERGE (not replace) the given `state`/`checklist` keys into the
 *  caller's record; `merge: true` is sent explicitly even though the backend defaults to it. */
export async function putCoachingStateLive(patch: CoachingStatePatch): Promise<CoachingState> {
  return apiFetch<CoachingState>("/coaching-state", {
    method: "PUT",
    auth: true,
    body: { ...patch, merge: true },
  });
}

/** DELETE /api/coaching-state — resets the caller's ENTIRE coaching record (idempotent). Exposed
 *  as a "reset all coaching" option; the "?" panel's per-element "Replay" never calls this (no
 *  per-key delete exists). */
export async function deleteCoachingStateLive(): Promise<void> {
  await apiFetch("/coaching-state", { method: "DELETE", auth: true });
}
